/**
 * Admin Dashboard — apps.leferi.com/admin
 *
 * Three sub-views:
 *   /admin            — overview cards + links to detail pages
 *   /admin/youtube    — YouTube Downloader detail (per-user, trend, active)
 *   /admin/artifact   — Artifact app detail (per-user, recent, perms)
 *
 * Gated by /api/admin/whoami client-side (and by requireAdmin on the
 * API). The header's Admin link is rendered conditionally upstream, so
 * landing here as a non-admin happens only if someone types the URL —
 * we show a clean "not authorized" panel rather than crashing on the
 * 403 from the overview fetch.
 *
 * Design notes:
 *   - Tables use hairline borders + numeric figures in lf-num.
 *   - Quality / permission distribution rendered as inline stacked
 *     bars (no chart library — the data is too small to justify one).
 *   - Pink reserved for primary CTAs; tabular accents use brand-blue
 *     and a single brown rule.
 */
import { Fragment, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { ChevronLeft, RefreshCw } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { PORTAL_LABELS } from './apps'
import {
  fetchAiTokenLogs,
  fetchAiTokens,
  fetchArtifact,
  fetchOAuthStatus,
  fetchOAuthStatusDetail,
  fetchTokenDiag,
  fetchOverview,
  fetchUsers,
  fetchYoutube,
  setUserAccess,
  setUserAdmin,
  formatBytes,
  formatDate,
  formatNextRun,
  formatRelKo,
  issueAiToken,
  postOAuthCollect,
  revokeAiToken,
  fetchCreators,
  postCreator,
  deleteCreator,
  patchCreatorMeta,
  postOAuthInvite,
  putCreatorChannel,
  deleteCreatorChannel,
  type AdminCreator,
  type AdminChannel,
  type ChannelPlatform,
  type AiTokenIssueResponse,
  type AiTokenLogRow,
  type AiTokenRow,
  type AiTokenStatus,
  type ArtifactResponse,
  type CreatorMonitorDetail,
  type CreatorMonitorRow,
  type TokenDiagResult,
  type CreatorOAuthStatus,
  type OAuthStatusResponse,
  type OverviewResponse,
  type PendingInviteRow,
  type PortalAccessInput,
  type UsersResponse,
  type YoutubeResponse,
} from './adminApi'

/**
 * Bilingual label helper. Major items (page titles, section headers,
 * top-level stat cards) render as `한국어 · English`. Small accessory
 * labels stay English-only by design — the CEO asked for the main
 * stuff bilingual, not every chip.
 */
function bi(ko: string, en: string): string {
  return `${ko} · ${en}`
}

/* -------------------------------------------------------------------------- */
/* Layout chrome                                                              */
/* -------------------------------------------------------------------------- */

function BackLink() {
  return (
    <Link
      to="/"
      className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:bg-surface-soft hover:text-fg-1 focus-visible:outline-none focus-visible:shadow-focus"
    >
      <ChevronLeft size={14} strokeWidth={1.5} />
      Apps
    </Link>
  )
}

function AdminTabs() {
  const loc = useLocation()
  const path = loc.pathname
  const tabs: { to: string; label: string }[] = [
    { to: '/admin', label: bi('전체 요약', 'Overview') },
    { to: '/admin/users', label: bi('사용자', 'Users') },
    { to: '/admin/youtube', label: 'YouTube' },
    { to: '/admin/artifact', label: 'Artifact' },
    { to: '/admin/creators', label: bi('크리에이터 관리', 'Creator Dashboards') },
    { to: '/admin/oauth', label: bi('OAuth 모니터', 'OAuth Monitor') },
    { to: '/admin/ai-tokens', label: bi('AI 토큰', 'AI Tokens') },
  ]
  return (
    <div className="mt-s-6 flex flex-wrap gap-s-2">
      {tabs.map((t) => {
        const active = path === t.to
        return (
          <Link
            key={t.to}
            to={t.to}
            className={`rounded-r-1 border px-s-3 py-s-2 lf-body-sm transition-colors duration-base ease-standard focus-visible:outline-none focus-visible:shadow-focus ${
              active
                ? 'border-brand-pink bg-pink-50 text-fg-1'
                : 'border-line-hairline bg-surface-base text-fg-2 hover:border-line-soft hover:text-fg-1'
            }`}
          >
            {t.label}
          </Link>
        )
      })}
    </div>
  )
}

function PageHeader({
  eyebrow,
  title,
  onRefresh,
}: {
  eyebrow: string
  title: string
  onRefresh?: () => void
}) {
  return (
    <div className="mt-s-5 flex items-end justify-between gap-s-4">
      <div>
        <p className="lf-eyebrow">▪ {eyebrow}</p>
        <h1 className="mt-s-2 lf-display-2">{title}</h1>
      </div>
      {onRefresh ? (
        <button
          type="button"
          onClick={onRefresh}
          className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1 focus-visible:outline-none focus-visible:shadow-focus"
        >
          <RefreshCw size={14} strokeWidth={1.5} />
          Refresh
        </button>
      ) : null}
    </div>
  )
}

function NotAdminPanel() {
  return (
    <div className="pt-s-7">
      <BackLink />
      <div className="mt-s-9 border border-line-hairline p-s-9 text-center">
        <p className="lf-eyebrow">▪ Forbidden</p>
        <h1 className="mt-s-3 lf-display-2">Admins only.</h1>
        <p className="mt-s-4 mx-auto max-w-[480px] lf-body text-fg-2">
          이 페이지는 관리자만 접근할 수 있어. 권한이 필요하면 CEO에게
          문의해.
        </p>
      </div>
    </div>
  )
}

function LoadingShell({ label }: { label: string }) {
  return <div className="mt-s-6 lf-body text-fg-3">{label}</div>
}

/* -------------------------------------------------------------------------- */
/* Overview                                                                   */
/* -------------------------------------------------------------------------- */

export function AdminOverviewPage() {
  const [data, setData] = useState<OverviewResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let cancelled = false
    setError(null)
    setData(null)
    fetchOverview()
      .then((d) => {
        if (!cancelled) setData(d)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const msg = err instanceof Error ? err.message : String(err)
        setError(msg)
      })
    return () => {
      cancelled = true
    }
  }, [tick])

  if (error?.includes('403')) return <NotAdminPanel />

  return (
    <div className="pt-s-7">
      <BackLink />
      <PageHeader
        eyebrow={bi('어드민', 'Admin')}
        title={bi('전체 요약', 'Overview')}
        onRefresh={() => setTick((t) => t + 1)}
      />
      <AdminTabs />

      {error ? (
        <p className="mt-s-6 lf-body-sm text-status-danger">▪ {error}</p>
      ) : null}

      {!data && !error ? <LoadingShell label="Loading." /> : null}

      {data ? (
        <>
          <Section title={bi('사용자', 'Users')}>
            <div className="grid grid-cols-2 gap-s-3 sm:grid-cols-4">
              <StatCard
                label={bi('전체 가입자', 'Total signups')}
                value={data.users.total.toLocaleString()}
              />
              <StatCard
                label={bi('최근 7일 가입', 'Signups · 7d')}
                value={data.users.last7d.toLocaleString()}
              />
              <StatCard
                label={bi('최근 30일 가입', 'Signups · 30d')}
                value={data.users.last30d.toLocaleString()}
              />
              <StatCard
                label={bi('최근 24h 활동', 'Active · 24h')}
                value={data.users.activeLast24h.toLocaleString()}
                hint="Profile touched in last 24h (login or token refresh)."
              />
            </div>
            <SignupTrend data={data.users.signupTrend} />
            <p className="mt-s-3 lf-body-sm">
              <Link
                to="/admin/users"
                className="text-brand-pink underline underline-offset-4"
              >
                {bi('전체 사용자 목록', 'See all users')} →
              </Link>
            </p>
          </Section>

          <Section title={bi('유튜브 다운로더', 'YouTube Downloader')}>
            <div className="grid grid-cols-2 gap-s-3 sm:grid-cols-4">
              <StatCard
                label={bi('전체 다운로드', 'Downloads · all-time')}
                value={data.youtube.totalDownloads.toLocaleString()}
              />
              <StatCard
                label={bi('최근 7일', 'Downloads · 7d')}
                value={data.youtube.last7d.toLocaleString()}
              />
              <StatCard
                label={bi('총 다운로드 용량', 'Bytes shipped')}
                value={formatBytes(data.youtube.totalBytes)}
                hint="Sum of completed file sizes — proxy egress + S3 traffic cost proxy."
              />
              <StatCard
                label={bi('진행 중', 'Active now')}
                value={data.youtube.activeNow.toLocaleString()}
              />
            </div>

            {data.youtube.topUsers.length > 0 ? (
              <div className="mt-s-5">
                <p className="lf-eyebrow text-fg-3">
                  ▪ {bi('가장 많이 사용한 사용자', 'Top users · by count')}
                </p>
                <ul className="mt-s-2 border border-line-hairline">
                  {data.youtube.topUsers.map((u, i) => (
                    <li
                      key={u.userSub}
                      className={`flex items-center justify-between px-s-3 py-s-2 lf-body-sm ${
                        i > 0 ? 'border-t border-line-hairline' : ''
                      }`}
                    >
                      <span className="text-fg-1">
                        <span className="lf-num mr-s-2 text-fg-3">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        {u.email}
                      </span>
                      <span className="lf-num text-fg-2">
                        {u.count.toLocaleString()} · {formatBytes(u.totalBytes)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <p className="mt-s-3 lf-body-sm">
              <Link
                to="/admin/youtube"
                className="text-brand-pink underline underline-offset-4"
              >
                {bi('YouTube 상세', 'YouTube detail')} →
              </Link>
            </p>
          </Section>

          <Section title={bi('아티팩트', 'Artifacts')}>
            <div className="grid grid-cols-2 gap-s-3 sm:grid-cols-4">
              <StatCard
                label={bi('전체 아티팩트', 'Artifacts')}
                value={data.artifact.total.toLocaleString()}
              />
              <StatCard
                label={bi('총 저장 용량', 'Storage')}
                value={formatBytes(data.artifact.totalBytes)}
              />
              <StatCard
                label={bi('총 조회수', 'Total views')}
                value={data.artifact.totalViews.toLocaleString()}
              />
            </div>
            <p className="mt-s-3 lf-body-sm">
              <Link
                to="/admin/artifact"
                className="text-brand-pink underline underline-offset-4"
              >
                {bi('Artifact 상세', 'Artifact detail')} →
              </Link>
            </p>
          </Section>

          <Section title={bi('PDF 압축', 'PDF Compressor')}>
            <div className="grid grid-cols-2 gap-s-3 sm:grid-cols-4">
              <StatCard
                label={bi('전체 압축', 'Jobs · all-time')}
                value={data.pdf.totalJobs.toLocaleString()}
              />
              <StatCard
                label={bi('최근 7일', 'Jobs · 7d')}
                value={data.pdf.last7d.toLocaleString()}
              />
              <StatCard
                label={bi('절감 용량', 'Bytes saved')}
                value={formatBytes(data.pdf.savedBytes)}
                hint="Sum of (original − compressed) across all completed jobs."
              />
              <StatCard
                label={bi('진행 중', 'Active now')}
                value={data.pdf.activeNow.toLocaleString()}
              />
            </div>
          </Section>
        </>
      ) : null}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Users — full list with joined per-user stats                               */
/* -------------------------------------------------------------------------- */

type UserSortKey =
  | 'createdAt'
  | 'lastSeenAt'
  | 'email'
  | 'ytCount'
  | 'ytBytes'
  | 'artifactCount'
  | 'artifactViews'

export function AdminUsersPage() {
  const [data, setData] = useState<UsersResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)
  const [sortKey, setSortKey] = useState<UserSortKey>('createdAt')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [filter, setFilter] = useState('')

  useEffect(() => {
    let cancelled = false
    setError(null)
    setData(null)
    fetchUsers()
      .then((d) => {
        if (!cancelled) setData(d)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : String(err))
      })
    return () => {
      cancelled = true
    }
  }, [tick])

  const sortedFiltered = useMemo(() => {
    if (!data) return []
    const q = filter.trim().toLowerCase()
    const filtered = q
      ? data.users.filter(
          (u) =>
            u.email.toLowerCase().includes(q) ||
            u.name.toLowerCase().includes(q),
        )
      : data.users
    const sorted = [...filtered].sort((a, b) => {
      const av = pluckSortValue(a, sortKey)
      const bv = pluckSortValue(b, sortKey)
      if (av === bv) return 0
      const cmp = av < bv ? -1 : 1
      return sortDir === 'asc' ? cmp : -cmp
    })
    return sorted
  }, [data, sortKey, sortDir, filter])

  if (error?.includes('403')) return <NotAdminPanel />

  const onSort = (key: UserSortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  return (
    <div className="pt-s-7">
      <BackLink />
      <PageHeader
        eyebrow={bi('어드민 · 사용자', 'Admin · Users')}
        title={bi('전체 사용자', 'All users')}
        onRefresh={() => setTick((t) => t + 1)}
      />
      <AdminTabs />

      {error ? (
        <p className="mt-s-6 lf-body-sm text-status-danger">▪ {error}</p>
      ) : null}
      {!data && !error ? <LoadingShell label="Loading." /> : null}

      {data ? (
        <>
          <Section title={bi('요약', 'Summary')}>
            <div className="grid grid-cols-2 gap-s-3 sm:grid-cols-4">
              <StatCard
                label={bi('전체 가입자', 'Total signups')}
                value={data.users.length.toLocaleString()}
              />
              <StatCard
                label={bi('YT 약관 동의', 'YouTube terms accepted')}
                value={data.users
                  .filter((u) => u.youtubeTermsAccepted)
                  .length.toLocaleString()}
              />
              <StatCard
                label={bi('YT 이용자 수', 'YT users')}
                value={data.users
                  .filter((u) => u.youtube.count > 0)
                  .length.toLocaleString()}
                hint="Users with at least one download attempt."
              />
              <StatCard
                label={bi('Artifact 이용자 수', 'Artifact users')}
                value={data.users
                  .filter((u) => u.artifact.count > 0)
                  .length.toLocaleString()}
                hint="Users with at least one artifact created."
              />
            </div>
          </Section>

          <Section
            title={bi(
              `사용자 목록 (${sortedFiltered.length})`,
              `User list (${sortedFiltered.length})`,
            )}
          >
            <div className="mb-s-3">
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={bi(
                  '이메일 또는 이름으로 검색',
                  'Filter by email or name',
                )}
                className="w-full max-w-md rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-1 placeholder:text-fg-3 focus:border-line-soft focus:outline-none"
              />
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-line-hairline">
                <thead className="bg-surface-soft">
                  <tr>
                    <SortableTh
                      active={sortKey === 'email'}
                      dir={sortDir}
                      onClick={() => onSort('email')}
                    >
                      User
                    </SortableTh>
                    <SortableTh
                      active={sortKey === 'createdAt'}
                      dir={sortDir}
                      onClick={() => onSort('createdAt')}
                      align="right"
                    >
                      Signed up
                    </SortableTh>
                    <SortableTh
                      active={sortKey === 'lastSeenAt'}
                      dir={sortDir}
                      onClick={() => onSort('lastSeenAt')}
                      align="right"
                    >
                      Last seen
                    </SortableTh>
                    <Th align="right">YT terms</Th>
                    <SortableTh
                      active={sortKey === 'ytCount'}
                      dir={sortDir}
                      onClick={() => onSort('ytCount')}
                      align="right"
                    >
                      YT count
                    </SortableTh>
                    <SortableTh
                      active={sortKey === 'ytBytes'}
                      dir={sortDir}
                      onClick={() => onSort('ytBytes')}
                      align="right"
                    >
                      YT bytes
                    </SortableTh>
                    <SortableTh
                      active={sortKey === 'artifactCount'}
                      dir={sortDir}
                      onClick={() => onSort('artifactCount')}
                      align="right"
                    >
                      Artifacts
                    </SortableTh>
                    <SortableTh
                      active={sortKey === 'artifactViews'}
                      dir={sortDir}
                      onClick={() => onSort('artifactViews')}
                      align="right"
                    >
                      Views
                    </SortableTh>
                    <Th>{bi('어드민', 'Admin')}</Th>
                    <Th>{bi('포털 접근 권한', 'Portal access')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFiltered.length === 0 ? (
                    <tr>
                      <td
                        colSpan={10}
                        className="px-s-3 py-s-5 lf-body-sm text-fg-3"
                      >
                        No matches.
                      </td>
                    </tr>
                  ) : (
                    sortedFiltered.map((u) => (
                      <tr
                        key={u.googleSub}
                        className="border-t border-line-hairline"
                      >
                        <Td>
                          <div className="flex flex-col">
                            <span className="text-fg-1">{u.email}</span>
                            {u.name ? (
                              <span className="lf-caption text-fg-3">
                                {u.name}
                              </span>
                            ) : null}
                          </div>
                        </Td>
                        <Td align="right">
                          <span className="lf-num text-fg-2">
                            {formatDate(u.createdAt)}
                          </span>
                        </Td>
                        <Td align="right">
                          <span className="lf-num text-fg-2">
                            {formatDate(u.lastSeenAt)}
                          </span>
                        </Td>
                        <Td align="right">
                          <span className="lf-num">
                            {u.youtubeTermsAccepted ? '✓' : '—'}
                          </span>
                        </Td>
                        <Td align="right">
                          <span className="lf-num">
                            {u.youtube.count.toLocaleString()}
                          </span>
                        </Td>
                        <Td align="right">
                          <span className="lf-num">
                            {formatBytes(u.youtube.totalBytes)}
                          </span>
                        </Td>
                        <Td align="right">
                          <span className="lf-num">
                            {u.artifact.count.toLocaleString()}
                          </span>
                        </Td>
                        <Td align="right">
                          <span className="lf-num">
                            {u.artifact.totalViews.toLocaleString()}
                          </span>
                        </Td>
                        <Td>
                          <AdminToggle
                            googleSub={u.googleSub}
                            initial={u.isAdmin}
                            isRoot={u.isRootAdmin}
                          />
                        </Td>
                        <Td>
                          <AccessEditor
                            googleSub={u.googleSub}
                            initial={u.portalAccess}
                          />
                        </Td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Section>
        </>
      ) : null}
    </div>
  )
}

/**
 * Inline per-user admin toggle. Root admins (ADMIN_EMAILS env) show a locked
 * "Owner" badge — their access is immutable here. Everyone else gets a
 * grant/revoke button that PATCHes immediately.
 */
function AdminToggle({
  googleSub,
  initial,
  isRoot,
}: {
  googleSub: string
  initial: boolean
  isRoot: boolean
}) {
  const [isAdmin, setIsAdmin] = useState(initial)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(false)

  if (isRoot) {
    return (
      <span
        className="inline-block rounded-r-1 border border-line-soft bg-surface-soft px-s-2 py-1 lf-caption text-fg-3"
        title="ADMIN_EMAILS 환경변수로 지정된 최상위 관리자 — 화면에서 변경 불가"
      >
        Owner
      </span>
    )
  }

  async function toggle() {
    if (saving) return
    const next = !isAdmin
    setSaving(true)
    setErr(false)
    setIsAdmin(next) // optimistic
    try {
      const r = await setUserAdmin(googleSub, next)
      setIsAdmin(r.isAdmin)
    } catch {
      setErr(true)
      setIsAdmin(initial) // revert
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex items-center gap-s-2">
      <button
        type="button"
        disabled={saving}
        onClick={toggle}
        className={[
          'rounded-r-1 border px-s-2 py-1 lf-caption transition-colors duration-base ease-standard disabled:opacity-50',
          isAdmin
            ? 'border-brand-pink bg-pink-50 text-fg-1'
            : 'border-line-soft bg-surface-base text-fg-2 hover:border-line-strong hover:text-fg-1',
        ].join(' ')}
        title={isAdmin ? '어드민 권한 회수' : '어드민 권한 부여'}
      >
        {saving ? '…' : isAdmin ? '✓ Admin' : 'Grant'}
      </button>
      {err ? <span className="lf-caption text-status-danger">실패</span> : null}
    </div>
  )
}

/**
 * Inline per-user portal access editor. "Everyone" toggles the for-Everyone
 * tab; each label chip toggles access to that label sub-tab under "Labels &
 * Creator Solutions". Each toggle PATCHes immediately and reflects the
 * server's canonical result.
 */
function AccessEditor({
  googleSub,
  initial,
}: {
  googleSub: string
  initial: PortalAccessInput
}) {
  const [everyone, setEveryone] = useState(initial.everyone)
  const [labels, setLabels] = useState<string[]>(initial.labels)
  const [bic, setBic] = useState(initial.bic === true)
  const [employee, setEmployee] = useState(initial.employee === true)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(false)

  async function commit(next: PortalAccessInput) {
    setSaving(true)
    setErr(false)
    // optimistic
    setEveryone(next.everyone)
    setLabels(next.labels)
    setBic(next.bic)
    setEmployee(next.employee)
    try {
      const r = await setUserAccess(googleSub, next)
      setEveryone(r.portalAccess.everyone)
      setLabels(r.portalAccess.labels)
      setBic(r.portalAccess.bic)
      setEmployee(r.portalAccess.employee)
    } catch {
      setErr(true)
      // revert
      setEveryone(initial.everyone)
      setLabels(initial.labels)
      setBic(initial.bic)
      setEmployee(initial.employee)
    } finally {
      setSaving(false)
    }
  }

  function toggleEveryone() {
    void commit({ everyone: !everyone, labels, bic, employee })
  }
  function toggleBic() {
    void commit({ everyone, labels, bic: !bic, employee })
  }
  function toggleEmployee() {
    void commit({ everyone, labels, bic, employee: !employee })
  }
  function toggleLabel(key: string) {
    const next = labels.includes(key)
      ? labels.filter((l) => l !== key)
      : labels.concat(key)
    void commit({ everyone, labels: next, bic, employee })
  }

  const chip = (active: boolean) =>
    [
      'border px-s-2 py-1 lf-caption transition-colors duration-base ease-standard',
      saving ? 'opacity-60' : '',
      active
        ? 'border-brand-blue bg-brand-blue text-fg-inverse'
        : 'border-line-soft bg-surface-base text-fg-2 hover:border-line-strong hover:text-fg-1',
    ].join(' ')

  return (
    <div className="flex flex-wrap items-center gap-s-1" style={{ maxWidth: 360 }}>
      <button
        type="button"
        disabled={saving}
        onClick={toggleEveryone}
        className={chip(everyone)}
        title="for Everyone 탭 접근"
      >
        Everyone
      </button>
      {/* 전사 직원 — for Leferist 탭과 그 안의 직원 전용 앱을 연다. */}
      <button
        type="button"
        disabled={saving}
        onClick={toggleEmployee}
        className={chip(employee)}
        title="전사 직원 — for Leferist 탭(Salesforce Products · 확인서 발송) 접근"
      >
        Employee
      </button>
      <span className="mx-1 h-4 w-px bg-line-hairline" aria-hidden />
      {PORTAL_LABELS.map((l) => (
        <button
          key={l.key}
          type="button"
          disabled={saving}
          onClick={() => toggleLabel(l.key)}
          className={chip(labels.includes(l.key))}
          title={`${l.name} 레이블 접근`}
        >
          {l.name}
        </button>
      ))}
      {/* BIC 는 레이블이 아니라 최상위 카테고리다 — 레이블 칩들 뒤에 구분선을 두고 분리한다.
          노출 조건은 레이블 칩과 같다(항상) — 부여 여부가 곧 접근 여부다. */}
      <span className="mx-1 h-4 w-px bg-line-hairline" aria-hidden />
      <button
        type="button"
        disabled={saving}
        onClick={toggleBic}
        className={chip(bic)}
        title="for Brand Intelligence Consulting 탭 + Creators 워크스페이스 접근"
      >
        BIC
      </button>
      {err ? <span className="lf-caption text-status-danger">저장 실패</span> : null}
    </div>
  )
}

function pluckSortValue(u: UsersResponse['users'][number], key: UserSortKey): number | string {
  switch (key) {
    case 'createdAt':
      return new Date(u.createdAt).getTime()
    case 'lastSeenAt':
      return new Date(u.lastSeenAt).getTime()
    case 'email':
      return u.email.toLowerCase()
    case 'ytCount':
      return u.youtube.count
    case 'ytBytes':
      return u.youtube.totalBytes
    case 'artifactCount':
      return u.artifact.count
    case 'artifactViews':
      return u.artifact.totalViews
  }
}

function SortableTh({
  children,
  active,
  dir,
  onClick,
  align = 'left',
}: {
  children: React.ReactNode
  active: boolean
  dir: 'asc' | 'desc'
  onClick: () => void
  align?: 'left' | 'right'
}) {
  return (
    <th
      scope="col"
      className={`px-s-3 py-s-2 lf-eyebrow ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-s-1 transition-colors duration-base ease-standard ${
          active ? 'text-fg-1' : 'text-fg-3 hover:text-fg-2'
        }`}
      >
        <span>{children}</span>
        {active ? (
          <span aria-hidden className="lf-num">
            {dir === 'asc' ? '↑' : '↓'}
          </span>
        ) : null}
      </button>
    </th>
  )
}

/* -------------------------------------------------------------------------- */
/* YouTube detail                                                             */
/* -------------------------------------------------------------------------- */

export function AdminYoutubePage() {
  const [data, setData] = useState<YoutubeResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let cancelled = false
    setError(null)
    setData(null)
    fetchYoutube()
      .then((d) => {
        if (!cancelled) setData(d)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : String(err))
      })
    return () => {
      cancelled = true
    }
  }, [tick])

  if (error?.includes('403')) return <NotAdminPanel />

  const trendMax = data ? Math.max(1, ...data.trend.map((p) => p.count)) : 1

  return (
    <div className="pt-s-7">
      <BackLink />
      <PageHeader
        eyebrow={bi('어드민 · YouTube', 'Admin · YouTube')}
        title={bi('유튜브 다운로더', 'YouTube Downloader')}
        onRefresh={() => setTick((t) => t + 1)}
      />
      <AdminTabs />

      {error ? (
        <p className="mt-s-6 lf-body-sm text-status-danger">▪ {error}</p>
      ) : null}
      {!data && !error ? <LoadingShell label="Loading." /> : null}

      {data ? (
        <>
          <Section title={bi('주요 지표', 'Key metrics')}>
            <div className="grid grid-cols-2 gap-s-3 sm:grid-cols-4">
              <StatCard
                label={bi('성공률', 'Success rate')}
                value={`${data.totals.successRate.toFixed(1)}%`}
                hint={`${data.totals.completedCount.toLocaleString()} / ${data.totals.totalCount.toLocaleString()}`}
              />
              <StatCard
                label={bi('완료된 다운로드', 'Completed')}
                value={data.totals.completedCount.toLocaleString()}
              />
              <StatCard
                label={bi('평균 파일 크기', 'Avg file size')}
                value={formatBytes(data.totals.avgBytes)}
              />
              <StatCard
                label={bi('총 다운로드 용량', 'Total bytes')}
                value={formatBytes(data.totals.completedBytes)}
              />
            </div>
          </Section>

          {data.active.length > 0 ? (
            <Section
              title={bi(
                `진행 중 (${data.active.length})`,
                `Active now (${data.active.length})`,
              )}
            >
              <div className="overflow-x-auto">
                <table className="min-w-full border border-line-hairline">
                  <thead className="bg-surface-soft">
                    <tr>
                      <Th>User</Th>
                      <Th>Status</Th>
                      <Th>Progress</Th>
                      <Th>Quality</Th>
                      <Th>Video</Th>
                      <Th>Started</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.active.map((row) => (
                      <tr
                        key={row.jobId}
                        className="border-t border-line-hairline"
                      >
                        <Td>{row.email}</Td>
                        <Td>
                          <span className="lf-num">{row.status}</span>
                        </Td>
                        <Td>
                          <span className="lf-num">{row.progress}%</span>
                        </Td>
                        <Td>
                          <span className="lf-num">{row.quality}</span>
                        </Td>
                        <Td>
                          <span className="line-clamp-1 text-fg-2">
                            {row.videoTitle || row.youtubeUrl}
                          </span>
                        </Td>
                        <Td>
                          <span className="lf-num text-fg-3">
                            {formatDate(row.createdAt)}
                          </span>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          ) : null}

          <Section title={bi('사용자별 사용량', 'Per-user usage')}>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-line-hairline">
                <thead className="bg-surface-soft">
                  <tr>
                    <Th>User</Th>
                    <Th align="right">Total</Th>
                    <Th align="right">Completed</Th>
                    <Th align="right">Bytes</Th>
                    <Th align="right">Last download</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.perUser.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-s-3 py-s-5 lf-body-sm text-fg-3">
                        No downloads yet.
                      </td>
                    </tr>
                  ) : (
                    data.perUser.map((row) => (
                      <tr
                        key={row.userSub}
                        className="border-t border-line-hairline"
                      >
                        <Td>{row.email}</Td>
                        <Td align="right">
                          <span className="lf-num">{row.count}</span>
                        </Td>
                        <Td align="right">
                          <span className="lf-num">{row.completedCount}</span>
                        </Td>
                        <Td align="right">
                          <span className="lf-num">
                            {formatBytes(row.totalBytes)}
                          </span>
                        </Td>
                        <Td align="right">
                          <span className="lf-num text-fg-3">
                            {formatDate(row.lastAt)}
                          </span>
                        </Td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Section>

          <Section title={bi('화질 분포', 'Quality distribution')}>
            <DistributionList
              rows={data.qualityDist.map((r) => ({
                label: r.quality,
                count: r.count,
              }))}
            />
          </Section>

          <Section title={bi('최근 30일 트렌드', 'Daily trend · last 30 days')}>
            <div className="flex items-end gap-px h-32 border-b border-line-hairline">
              {data.trend.length === 0 ? (
                <p className="lf-body-sm text-fg-3">No data.</p>
              ) : (
                data.trend.map((p) => {
                  const h = Math.max(2, (p.count / trendMax) * 100)
                  return (
                    <div
                      key={p.date}
                      title={`${p.date}: ${p.count} downloads · ${formatBytes(p.bytes)}`}
                      className="flex-1 bg-brand-pink/70 hover:bg-brand-pink"
                      style={{ height: `${h}%`, minWidth: '6px' }}
                    />
                  )
                })
              )}
            </div>
            {data.trend.length > 0 ? (
              <div className="mt-s-2 flex justify-between lf-eyebrow text-fg-3">
                <span className="lf-num">{data.trend[0]?.date}</span>
                <span className="lf-num">
                  {data.trend[data.trend.length - 1]?.date}
                </span>
              </div>
            ) : null}
          </Section>

          <Section title={bi('상태 분포', 'Status distribution')}>
            <DistributionList
              rows={data.statusDist.map((r) => ({
                label: r.status,
                count: r.count,
              }))}
            />
          </Section>

          {data.errorDist.length > 0 ? (
            <Section title={bi('실패 사유', 'Failure reasons')}>
              <DistributionList
                rows={data.errorDist.map((r) => ({
                  label: r.error,
                  count: r.count,
                }))}
              />
            </Section>
          ) : null}
        </>
      ) : null}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Artifact detail                                                            */
/* -------------------------------------------------------------------------- */

export function AdminArtifactPage() {
  const [data, setData] = useState<ArtifactResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let cancelled = false
    setError(null)
    setData(null)
    fetchArtifact()
      .then((d) => {
        if (!cancelled) setData(d)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : String(err))
      })
    return () => {
      cancelled = true
    }
  }, [tick])

  if (error?.includes('403')) return <NotAdminPanel />

  return (
    <div className="pt-s-7">
      <BackLink />
      <PageHeader
        eyebrow={bi('어드민 · Artifact', 'Admin · Artifact')}
        title={bi('아티팩트', 'Artifacts')}
        onRefresh={() => setTick((t) => t + 1)}
      />
      <AdminTabs />

      {error ? (
        <p className="mt-s-6 lf-body-sm text-status-danger">▪ {error}</p>
      ) : null}
      {!data && !error ? <LoadingShell label="Loading." /> : null}

      {data ? (
        <>
          <Section title={bi('주요 지표', 'Key metrics')}>
            <div className="grid grid-cols-2 gap-s-3 sm:grid-cols-4">
              <StatCard
                label={bi('전체 아티팩트', 'Artifacts')}
                value={data.totals.total.toLocaleString()}
              />
              <StatCard
                label={bi('총 저장 용량', 'Storage')}
                value={formatBytes(data.totals.totalBytes)}
              />
              <StatCard
                label={bi('평균 크기', 'Avg size')}
                value={formatBytes(data.totals.avgBytes)}
              />
              <StatCard
                label={bi('총 조회수', 'Total views')}
                value={data.totals.totalViews.toLocaleString()}
              />
            </div>
          </Section>

          <Section title={bi('사용자별 사용량', 'Per-user usage')}>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-line-hairline">
                <thead className="bg-surface-soft">
                  <tr>
                    <Th>User</Th>
                    <Th align="right">Artifacts</Th>
                    <Th align="right">Bytes</Th>
                    <Th align="right">Last created</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.perUser.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-s-3 py-s-5 lf-body-sm text-fg-3">
                        No artifacts yet.
                      </td>
                    </tr>
                  ) : (
                    data.perUser.map((row) => (
                      <tr
                        key={row.ownerSub}
                        className="border-t border-line-hairline"
                      >
                        <Td>{row.email}</Td>
                        <Td align="right">
                          <span className="lf-num">{row.count}</span>
                        </Td>
                        <Td align="right">
                          <span className="lf-num">
                            {formatBytes(row.totalBytes)}
                          </span>
                        </Td>
                        <Td align="right">
                          <span className="lf-num text-fg-3">
                            {formatDate(row.lastAt)}
                          </span>
                        </Td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Section>

          <Section title={bi('권한 분포', 'Permission distribution')}>
            <DistributionList
              rows={data.permissionDist.map((r) => ({
                label: r.permission,
                count: r.count,
              }))}
            />
          </Section>

          <Section title={bi('타입 분포', 'Type distribution')}>
            <DistributionList
              rows={data.typeDist.map((r) => ({
                label: r.type,
                count: r.count,
              }))}
            />
          </Section>

          {data.mostViewed.length > 0 ? (
            <Section title={bi('가장 많이 본 아티팩트', 'Most viewed')}>
              <div className="overflow-x-auto">
                <table className="min-w-full border border-line-hairline">
                  <thead className="bg-surface-soft">
                    <tr>
                      <Th>#</Th>
                      <Th>Title</Th>
                      <Th>Owner</Th>
                      <Th>Permission</Th>
                      <Th align="right">Views</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.mostViewed.map((row, i) => (
                      <tr
                        key={row.publicId}
                        className="border-t border-line-hairline"
                      >
                        <Td>
                          <span className="lf-num text-fg-3">
                            {String(i + 1).padStart(2, '0')}
                          </span>
                        </Td>
                        <Td>
                          <span className="line-clamp-1">{row.title}</span>
                        </Td>
                        <Td>{row.ownerEmail}</Td>
                        <Td>
                          <span className="lf-num">{row.permission}</span>
                        </Td>
                        <Td align="right">
                          <span className="lf-num">
                            {row.viewCount.toLocaleString()}
                          </span>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          ) : null}

          <Section title={bi('최근 업로드', 'Recent uploads')}>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-line-hairline">
                <thead className="bg-surface-soft">
                  <tr>
                    <Th>Title</Th>
                    <Th>Owner</Th>
                    <Th>Type</Th>
                    <Th>Permission</Th>
                    <Th align="right">Bytes</Th>
                    <Th align="right">Created</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent.map((row) => (
                    <tr
                      key={row.publicId}
                      className="border-t border-line-hairline"
                    >
                      <Td>
                        <span className="line-clamp-1">{row.title}</span>
                      </Td>
                      <Td>{row.ownerEmail}</Td>
                      <Td>
                        <span className="lf-num">{row.type}</span>
                      </Td>
                      <Td>
                        <span className="lf-num">{row.permission}</span>
                      </Td>
                      <Td align="right">
                        <span className="lf-num">
                          {formatBytes(row.sizeBytes)}
                        </span>
                      </Td>
                      <Td align="right">
                        <span className="lf-num text-fg-3">
                          {formatDate(row.createdAt)}
                        </span>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </>
      ) : null}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Atoms                                                                      */
/* -------------------------------------------------------------------------- */

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section className="mt-s-7">
      <h2 className="lf-h4 text-fg-1">{title}</h2>
      <div className="mt-s-3">{children}</div>
    </section>
  )
}

function StatCard({
  label,
  value,
  hint,
}: {
  label: string
  value: string
  hint?: string
}) {
  return (
    <div className="border border-line-hairline bg-surface-base p-s-4">
      <p className="lf-eyebrow text-fg-3">{label}</p>
      <p className="mt-s-2 lf-display-2 lf-num text-fg-1">{value}</p>
      {hint ? <p className="mt-s-2 lf-caption text-fg-3">{hint}</p> : null}
    </div>
  )
}

/**
 * Modal-only KPI card variant. Uses inline clamp() font-sizing so a
 * card holds either a long ISO datetime (e.g. "2026-06-01, 15:40") OR
 * a bilingual label ("자동 갱신 중 · Auto-refresh") on a single line at
 * every breakpoint without overflowing or breaking the digits across
 * lines. Three rules apply:
 *
 *   - Plain `value`  → clamp(18px, 2.2vw, 28px), nowrap + ellipsis.
 *     Wide enough at 220px for "2026-06-01, 15:40" with Pretendard
 *     tabular numerals.
 *   - `valueKo` + `valueEn` (bi-format) → clamp(14px, 1.6vw, 20px),
 *     English suffix renders 0.85em + muted color, word-break: keep-all
 *     so the dot separator stays attached.
 *   - All cards: min-height 120px, flex-col, optional hint pushed to
 *     the bottom via mt-auto so heights match across the row.
 */
function ModalKpiCard({
  label,
  value,
  valueKo,
  valueEn,
  hint,
}: {
  label: string
  value?: string
  valueKo?: string
  valueEn?: string
  hint?: string
}) {
  const isBi = !!valueKo && !!valueEn
  const fontSize = isBi ? 'clamp(14px, 1.6vw, 20px)' : 'clamp(18px, 2.2vw, 28px)'
  return (
    <div className="flex min-h-[120px] flex-col border border-line-hairline bg-surface-base p-s-4">
      <p className="lf-eyebrow text-fg-3">{label}</p>
      <p
        className="mt-s-2 lf-num text-fg-1"
        style={{
          fontSize,
          fontWeight: 700,
          fontFamily:
            'var(--font-display, "Grato Classic"), "Pretendard Variable", "Pretendard", -apple-system, BlinkMacSystemFont, sans-serif',
          lineHeight: 1.2,
          wordBreak: 'keep-all',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {isBi ? (
          <>
            <span>{valueKo}</span>
            <span className="text-fg-3" style={{ fontSize: '0.85em', fontWeight: 600 }}>
              {' · '}
              {valueEn}
            </span>
          </>
        ) : (
          value ?? '—'
        )}
      </p>
      {hint ? <p className="mt-auto pt-s-2 lf-caption text-fg-3">{hint}</p> : null}
    </div>
  )
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode
  align?: 'left' | 'right'
}) {
  return (
    <th
      scope="col"
      className={`px-s-3 py-s-2 lf-eyebrow text-fg-3 ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      {children}
    </th>
  )
}

function Td({
  children,
  align = 'left',
}: {
  children: React.ReactNode
  align?: 'left' | 'right'
}) {
  return (
    <td
      className={`px-s-3 py-s-2 lf-body-sm text-fg-1 ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      {children}
    </td>
  )
}

function SignupTrend({ data }: { data: { date: string; count: number }[] }) {
  if (!data || data.length === 0) {
    return (
      <p className="mt-s-4 lf-body-sm text-fg-3">
        ▪ No signups in the last 30 days.
      </p>
    )
  }
  const max = Math.max(1, ...data.map((p) => p.count))
  return (
    <div className="mt-s-5">
      <p className="lf-eyebrow text-fg-3">
        ▪ {bi('최근 30일 가입 추이', 'Signups · last 30 days')}
      </p>
      <div className="mt-s-2 flex items-end gap-px h-24 border-b border-line-hairline">
        {data.map((p) => {
          const h = Math.max(2, (p.count / max) * 100)
          return (
            <div
              key={p.date}
              title={`${p.date}: ${p.count} new`}
              className="flex-1 bg-brand-blue/70 hover:bg-brand-blue"
              style={{ height: `${h}%`, minWidth: '6px' }}
            />
          )
        })}
      </div>
      <div className="mt-s-2 flex justify-between lf-eyebrow text-fg-3">
        <span className="lf-num">{data[0]?.date}</span>
        <span className="lf-num">{data[data.length - 1]?.date}</span>
      </div>
    </div>
  )
}

function DistributionList({
  rows,
}: {
  rows: { label: string; count: number }[]
}) {
  const total = rows.reduce((acc, r) => acc + r.count, 0)
  if (total === 0) {
    return <p className="lf-body-sm text-fg-3">No data.</p>
  }
  return (
    <ul className="flex flex-col gap-s-2">
      {rows.map((r) => {
        const pct = (r.count / total) * 100
        return (
          <li key={r.label} className="flex items-center gap-s-3">
            <span className="w-24 lf-body-sm text-fg-2 lf-num">{r.label}</span>
            <div className="flex-1 h-2 bg-surface-soft">
              <div
                className="h-full bg-brand-blue"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="w-16 text-right lf-body-sm lf-num text-fg-2">
              {r.count} ({pct.toFixed(0)}%)
            </span>
          </li>
        )
      })}
    </ul>
  )
}

/* -------------------------------------------------------------------------- */
/* OAuth Monitor — /admin/oauth                                               */
/* -------------------------------------------------------------------------- */

type StatusFilter = CreatorOAuthStatus | 'all'
type SortKey = 'name' | 'connected' | 'lastCollected' | 'rows'

const STATUS_BADGE: Record<CreatorOAuthStatus, { label: string; cls: string }> = {
  active: {
    label: 'Active',
    cls: 'bg-green-50 text-green-700 border-green-200',
  },
  expired: {
    label: 'Expired',
    cls: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  never_connected: {
    label: 'Never',
    cls: 'bg-surface-soft text-fg-3 border-line-soft',
  },
  revoked: {
    label: 'Error',
    cls: 'bg-red-50 text-red-700 border-red-200',
  },
}

export function AdminOAuthPage() {
  const [data, setData] = useState<OAuthStatusResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [query, setQuery] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('connected')
  const [pageSize] = useState(50)
  const [pageIndex, setPageIndex] = useState(0)
  const [detail, setDetail] = useState<CreatorMonitorDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [busyHandles, setBusyHandles] = useState<Record<string, boolean>>({})
  const [toast, setToast] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    let cancelled = false
    setData(null)
    setError(null)
    fetchOAuthStatus({
      status: statusFilter,
      q: query.trim() || undefined,
      limit: 200,
      offset: 0,
      showAll,
    })
      .then((d) => {
        if (!cancelled) setData(d)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const msg = err instanceof Error ? err.message : String(err)
        setError(msg)
      })
    return () => {
      cancelled = true
    }
  }, [tick, statusFilter, query, showAll])

  // Client-side sort on the API's (filtered) creator list. Pagination on
  // top of that — every creator is in memory; cheaper than round-trips
  // for fewer than ~200 rows.
  const sortedRows = useMemo<CreatorMonitorRow[]>(() => {
    if (!data) return []
    const copy = [...data.creators]
    copy.sort((a, b) => {
      if (sortKey === 'name') {
        const ax = (a.displayNameKr || a.displayNameEn || a.channelName || a.handle).toLowerCase()
        const bx = (b.displayNameKr || b.displayNameEn || b.channelName || b.handle).toLowerCase()
        return ax.localeCompare(bx)
      }
      if (sortKey === 'connected') {
        return tsDesc(a.connectedAt, b.connectedAt)
      }
      if (sortKey === 'lastCollected') {
        return tsDesc(a.lastCollectedAt, b.lastCollectedAt)
      }
      // rows
      return b.collectedRowCount - a.collectedRowCount
    })
    return copy
  }, [data, sortKey])

  const paged = useMemo(() => {
    const start = pageIndex * pageSize
    return sortedRows.slice(start, start + pageSize)
  }, [sortedRows, pageIndex, pageSize])

  if (error?.includes('403')) return <NotAdminPanel />

  const openDetail = async (handle: string) => {
    setDetail(null)
    setDetailLoading(true)
    try {
      const d = await fetchOAuthStatusDetail(handle)
      setDetail(d)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setError(msg)
    } finally {
      setDetailLoading(false)
    }
  }

  const triggerCollect = async (handle: string) => {
    setBusyHandles((s) => ({ ...s, [handle]: true }))
    try {
      const r = await postOAuthCollect(handle)
      if ('error' in r && r.error === 'already_in_progress') {
        setToast(r.message)
      } else if ('success' in r) {
        setToast(
          r.success
            ? `${handle}: ${r.collectedRows.toLocaleString()}건 · ${Math.round(r.durationMs / 1000)}초`
            : `${handle}: 실패 (${r.errors.join('; ')})`,
        )
        // Refresh the list so the row reflects the new lastCollectedAt.
        setTick((t) => t + 1)
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setToast(`${handle}: ${msg}`)
    } finally {
      setBusyHandles((s) => {
        const next = { ...s }
        delete next[handle]
        return next
      })
      setTimeout(() => setToast(null), 4000)
    }
  }

  return (
    <div className="pt-s-7">
      <BackLink />
      <PageHeader
        eyebrow={bi('어드민', 'Admin')}
        title={bi('OAuth 모니터', 'OAuth Monitor')}
        onRefresh={() => setTick((t) => t + 1)}
      />
      <AdminTabs />

      {error ? (
        <p className="mt-s-6 lf-body-sm text-status-danger">▪ {error}</p>
      ) : null}
      {!data && !error ? <LoadingShell label="Loading." /> : null}

      {data ? (
        <>
          {/* === System Status ============================================ */}
          <Section title={bi('시스템 상태', 'System Status')}>
            <SystemStatusBar status={data.systemStatus} />
          </Section>

          {/* === Filter row ============================================== */}
          <Section title={bi('크리에이터', 'Creators')}>
            <FilterRow
              statusFilter={statusFilter}
              onStatusFilter={(s) => {
                setStatusFilter(s)
                setPageIndex(0)
              }}
              query={query}
              onQuery={(q) => {
                setQuery(q)
                setPageIndex(0)
              }}
              sortKey={sortKey}
              onSortKey={setSortKey}
              counts={data.systemStatus}
              showAll={showAll}
              onShowAll={(v) => {
                setShowAll(v)
                setPageIndex(0)
              }}
            />

            {sortedRows.length === 0 ? (
              <div className="mt-s-5 border border-dashed border-line-soft bg-surface-soft p-s-7 text-center">
                <p className="lf-eyebrow text-fg-3">▪ Empty</p>
                <p className="mt-s-2 lf-body text-fg-2">
                  {query || statusFilter !== 'all'
                    ? '필터에 해당하는 크리에이터가 없어요.'
                    : '아직 등록된 크리에이터가 없어요. invite 토큰을 발급해 첫 연결을 시작하세요.'}
                </p>
              </div>
            ) : (
              <>
                <div className="mt-s-5 grid grid-cols-1 gap-s-3 sm:grid-cols-2 lg:grid-cols-3">
                  {paged.map((c) => (
                    <CreatorCard
                      key={c.handle}
                      row={c}
                      busy={!!busyHandles[c.handle]}
                      onDetail={() => openDetail(c.handle)}
                      onCollect={() => triggerCollect(c.handle)}
                    />
                  ))}
                </div>
                {sortedRows.length > pageSize ? (
                  <Pagination
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    total={sortedRows.length}
                    onPage={setPageIndex}
                  />
                ) : null}
              </>
            )}
          </Section>

          {/* === Pending Invites ========================================= */}
          <Section title={bi('대기 중 초대', 'Pending Invites')}>
            <PendingInvitesList invites={data.pendingInvites} onToast={setToast} />
          </Section>
        </>
      ) : null}

      {/* === Detail modal ============================================= */}
      {detail || detailLoading ? (
        <CreatorDetailModal
          loading={detailLoading}
          detail={detail}
          busy={detail ? !!busyHandles[detail.handle] : false}
          onClose={() => {
            setDetail(null)
            setDetailLoading(false)
          }}
          onCollect={() => detail && triggerCollect(detail.handle)}
        />
      ) : null}

      {/* === Toast ==================================================== */}
      {toast ? (
        <div className="fixed bottom-s-6 left-1/2 z-50 -translate-x-1/2 border border-line-soft bg-bg-ink px-s-4 py-s-3 lf-body-sm text-fg-inverse shadow-lf-1">
          {toast}
        </div>
      ) : null}
    </div>
  )
}

function tsDesc(a: string | null, b: string | null): number {
  const ax = a ? new Date(a).getTime() : -Infinity
  const bx = b ? new Date(b).getTime() : -Infinity
  return bx - ax
}

function SystemStatusBar({
  status,
}: {
  status: OAuthStatusResponse['systemStatus']
}) {
  return (
    <div className="border border-line-hairline bg-surface-base p-s-4">
      <div className="flex flex-wrap items-center gap-s-4">
        <span className="inline-flex items-center gap-s-2">
          <span
            className={`inline-block h-2 w-2 ${
              status.cronHealthy ? 'bg-status-success' : 'bg-status-danger'
            }`}
            aria-hidden
          />
          <span className="lf-body-sm text-fg-1">
            Cron {status.cronHealthy ? bi('정상', 'Healthy') : bi('점검 필요', 'Check')}
          </span>
        </span>
        <span className="lf-body-sm text-fg-3">
          {bi('마지막 실행', 'Last run')}:{' '}
          <span className="lf-num text-fg-1">
            {status.lastCronRunAt ? formatRelKo(status.lastCronRunAt) : '—'}
          </span>
        </span>
        <span className="lf-body-sm text-fg-3">
          {bi('다음 예정', 'Next at')}:{' '}
          <span className="lf-num text-fg-1">
            {formatNextRun(status.nextCronRunAt)}
          </span>
        </span>
      </div>
      <div className="mt-s-4 grid grid-cols-2 gap-s-2 sm:grid-cols-5">
        <StatCard label={bi('전체', 'Total')} value={String(status.totalCreators)} />
        <StatCard label={bi('연결됨', 'Active')} value={String(status.activeCount)} />
        <StatCard label={bi('만료', 'Expired')} value={String(status.expiredCount)} />
        <StatCard label={bi('미연결', 'Never')} value={String(status.neverCount)} />
        <StatCard label={bi('오류', 'Errors')} value={String(status.errorCount)} />
      </div>
    </div>
  )
}

function FilterRow({
  statusFilter,
  onStatusFilter,
  query,
  onQuery,
  sortKey,
  onSortKey,
  counts,
  showAll,
  onShowAll,
}: {
  statusFilter: StatusFilter
  onStatusFilter: (s: StatusFilter) => void
  query: string
  onQuery: (q: string) => void
  sortKey: SortKey
  onSortKey: (k: SortKey) => void
  counts: OAuthStatusResponse['systemStatus']
  showAll: boolean
  onShowAll: (v: boolean) => void
}) {
  const chips: { v: StatusFilter; label: string; count: number }[] = [
    { v: 'all', label: 'All', count: counts.totalCreators },
    { v: 'active', label: 'Active', count: counts.activeCount },
    { v: 'expired', label: 'Expired', count: counts.expiredCount },
    { v: 'never_connected', label: 'Never', count: counts.neverCount },
    { v: 'revoked', label: 'Errors', count: counts.errorCount },
  ]
  return (
    <div className="flex flex-wrap items-center gap-s-3">
      <div className="flex flex-wrap gap-s-2">
        {chips.map((c) => {
          const active = statusFilter === c.v
          return (
            <button
              key={c.v}
              type="button"
              onClick={() => onStatusFilter(c.v)}
              className={`rounded-r-1 border px-s-3 py-s-2 lf-body-sm transition-colors duration-base ease-standard ${
                active
                  ? 'border-brand-pink bg-pink-50 text-fg-1'
                  : 'border-line-hairline bg-surface-base text-fg-2 hover:border-line-soft hover:text-fg-1'
              }`}
            >
              {c.label}{' '}
              <span className="ml-s-1 lf-num text-fg-3">({c.count})</span>
            </button>
          )
        })}
      </div>
      <div className="ml-auto flex flex-wrap items-center gap-s-2">
        <label className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 cursor-pointer hover:border-line-soft hover:text-fg-1">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => onShowAll(e.target.checked)}
            className="h-3 w-3 accent-brand-pink"
          />
          <span>{bi('비공식 포함', 'Show All')}</span>
        </label>
        <input
          type="search"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="검색 · handle, 채널명"
          className="rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-1 focus:outline-none focus:border-brand-pink"
        />
        <select
          value={sortKey}
          onChange={(e) => onSortKey(e.target.value as SortKey)}
          className="rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-1 focus:outline-none focus:border-brand-pink"
        >
          <option value="connected">{bi('정렬: 최근 연결순', 'Recently connected')}</option>
          <option value="lastCollected">{bi('정렬: 최근 수집순', 'Recently collected')}</option>
          <option value="rows">{bi('정렬: 데이터 양순', 'Most rows')}</option>
          <option value="name">{bi('정렬: 이름순', 'Name')}</option>
        </select>
      </div>
    </div>
  )
}

function CreatorCard({
  row,
  busy,
  onDetail,
  onCollect,
}: {
  row: CreatorMonitorRow
  busy: boolean
  onDetail: () => void
  onCollect: () => void
}) {
  const badge = STATUS_BADGE[row.status]
  const name =
    row.displayNameKr && row.displayNameEn
      ? `${row.displayNameKr} · ${row.displayNameEn}`
      : row.displayNameKr || row.displayNameEn || row.channelName || row.handle
  return (
    <div className="flex flex-col gap-s-3 border border-line-hairline bg-surface-base p-s-4">
      <div className="flex items-start gap-s-3">
        {row.profileImageUrl ? (
          <img
            src={row.profileImageUrl}
            alt=""
            referrerPolicy="no-referrer"
            className="h-10 w-10 rounded-r-pill bg-surface-soft object-cover"
          />
        ) : (
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-r-pill bg-surface-soft lf-eyebrow text-fg-3">
            ?
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate lf-body text-fg-1">{name}</p>
          <p className="lf-body-sm lf-num text-fg-3">{row.handle}</p>
        </div>
        <span
          className={`shrink-0 rounded-r-1 border px-s-2 py-1 lf-eyebrow ${badge.cls}`}
        >
          {badge.label}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-s-2 border-t border-line-hairline pt-s-3">
        <MiniMetric
          label={bi('마지막 수집', 'Last collect')}
          value={row.lastCollectedAt ? formatRelKo(row.lastCollectedAt) : '—'}
        />
        <MiniMetric
          label={bi('수집 행', 'Rows')}
          value={row.collectedRowCount.toLocaleString()}
        />
        <MiniMetric
          label={bi('다음 예정', 'Next')}
          value={formatNextRun(row.nextScheduledAt)}
        />
      </div>
      <div className="flex gap-s-2 pt-s-1">
        <button
          type="button"
          onClick={onDetail}
          className="flex-1 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 hover:border-line-soft hover:text-fg-1"
        >
          {bi('상세', 'Detail')}
        </button>
        <button
          type="button"
          onClick={onCollect}
          disabled={busy || row.status === 'never_connected' || row.status === 'revoked'}
          className="flex-1 rounded-r-1 border border-brand-pink bg-brand-pink px-s-3 py-s-2 lf-body-sm text-fg-inverse disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? bi('수집 중...', 'Collecting…') : bi('지금 수집', 'Collect now')}
        </button>
      </div>
    </div>
  )
}

function MiniMetric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="lf-eyebrow text-fg-3">{label}</p>
      <p className="mt-s-1 lf-body-sm lf-num text-fg-1">{value}</p>
    </div>
  )
}

function Pagination({
  pageIndex,
  pageSize,
  total,
  onPage,
}: {
  pageIndex: number
  pageSize: number
  total: number
  onPage: (n: number) => void
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize))
  return (
    <div className="mt-s-4 flex items-center justify-between gap-s-3">
      <p className="lf-body-sm text-fg-3">
        {bi('페이지', 'Page')}{' '}
        <span className="lf-num text-fg-1">{pageIndex + 1}</span> / {pages}
      </p>
      <div className="flex gap-s-2">
        <button
          type="button"
          disabled={pageIndex === 0}
          onClick={() => onPage(Math.max(0, pageIndex - 1))}
          className="rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 hover:text-fg-1 disabled:opacity-50"
        >
          {bi('이전', 'Prev')}
        </button>
        <button
          type="button"
          disabled={pageIndex + 1 >= pages}
          onClick={() => onPage(pageIndex + 1)}
          className="rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 hover:text-fg-1 disabled:opacity-50"
        >
          {bi('다음', 'Next')}
        </button>
      </div>
    </div>
  )
}

function PendingInvitesList({
  invites,
  onToast,
}: {
  invites: PendingInviteRow[]
  onToast: (msg: string) => void
}) {
  if (invites.length === 0) {
    return (
      <p className="lf-body-sm text-fg-3">
        {bi('대기 중인 초대가 없어요.', 'No pending invites.')}
      </p>
    )
  }
  const copy = (text: string) => {
    void navigator.clipboard.writeText(text).then(
      () => onToast('URL 복사됨'),
      () => onToast('복사에 실패했어요'),
    )
  }
  return (
    <ul className="border border-line-hairline">
      {invites.map((iv) => {
        const urgent = iv.daysToExpiry <= 3
        return (
          <li
            key={iv.handle + iv.expiresAt}
            className={`flex flex-wrap items-center gap-s-3 border-b border-line-hairline px-s-4 py-s-3 last:border-b-0 ${
              urgent ? 'bg-red-50' : 'bg-surface-base'
            }`}
          >
            <div className="min-w-0 flex-1">
              <p className="truncate lf-body text-fg-1">{iv.channelName}</p>
              <p className="lf-body-sm lf-num text-fg-3">{iv.handle}</p>
            </div>
            <p className="lf-body-sm text-fg-3">
              {bi('발급', 'Issued')}{' '}
              <span className="lf-num text-fg-1">{formatRelKo(iv.issuedAt)}</span>
            </p>
            <p className={`lf-body-sm ${urgent ? 'text-status-danger' : 'text-fg-3'}`}>
              {bi('만료까지', 'Expires in')}{' '}
              <span className="lf-num">{iv.daysToExpiry}일</span>
            </p>
            <button
              type="button"
              onClick={() => copy(iv.inviteUrl)}
              className="rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 hover:text-fg-1"
            >
              {bi('URL 복사', 'Copy URL')}
            </button>
          </li>
        )
      })}
    </ul>
  )
}

// 30s live-diag cache, keyed by handle. Survives modal remounts so a
// reopened detail within 30s reuses the last probe instead of hammering
// Google.
const DIAG_CACHE_MS = 30_000
const tokenDiagCache = new Map<string, { at: number; data: TokenDiagResult }>()

function CreatorDetailModal({
  loading,
  detail,
  busy,
  onClose,
  onCollect,
}: {
  loading: boolean
  detail: CreatorMonitorDetail | null
  busy: boolean
  onClose: () => void
  onCollect: () => void
}) {
  const handle = detail?.handle ?? ''
  const [diag, setDiag] = useState<TokenDiagResult | null>(null)
  const [diagBusy, setDiagBusy] = useState(false)
  const [diagErr, setDiagErr] = useState<string | null>(null)

  // On creator switch: reuse a fresh cached probe, else clear.
  useEffect(() => {
    if (!handle) {
      setDiag(null)
      setDiagErr(null)
      return
    }
    const c = tokenDiagCache.get(handle.toLowerCase())
    setDiag(c && Date.now() - c.at < DIAG_CACHE_MS ? c.data : null)
    setDiagErr(null)
  }, [handle])

  const runDiag = async () => {
    if (!handle || diagBusy) return
    const key = handle.toLowerCase()
    const cached = tokenDiagCache.get(key)
    if (cached && Date.now() - cached.at < DIAG_CACHE_MS) {
      setDiag(cached.data)
      setDiagErr(null)
      return
    }
    setDiagBusy(true)
    setDiagErr(null)
    try {
      const r = await fetchTokenDiag(handle)
      tokenDiagCache.set(key, { at: Date.now(), data: r })
      setDiag(r)
    } catch (e) {
      setDiagErr((e as Error).message || 'diag_failed')
    } finally {
      setDiagBusy(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/60 px-s-4"
    >
      {/* Modal container width — wider than LCMD's lmodal--xl (900px) on
          purpose: this modal hosts a 4-card KPI grid where each card
          carries bilingual labels + a long ISO datetime. At 720px the
          cards collapsed to ~180px each and shattered "2026-06-01,
          15:40" + "자동 갱신 중 · Auto-refresh" vertically. 1100px on
          desktop gives every card 240px+, enough for one-line text.
          Tablet keeps lmodal--xl behavior; mobile fills the viewport. */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] w-full max-w-[1100px] md:max-w-[720px] lg:max-w-[1100px] overflow-auto border border-line-hairline bg-surface-base p-s-6"
      >
        <div className="flex items-start justify-between gap-s-3">
          <p className="lf-eyebrow text-fg-3">▪ Creator Detail</p>
          <button
            type="button"
            onClick={onClose}
            className="rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-1 lf-body-sm text-fg-2 hover:text-fg-1"
          >
            ✕
          </button>
        </div>
        {loading || !detail ? (
          <LoadingShell label="Loading detail." />
        ) : (
          <>
            <div className="mt-s-3 flex items-start gap-s-3">
              {detail.profileImageUrl ? (
                <img
                  src={detail.profileImageUrl}
                  alt=""
                  referrerPolicy="no-referrer"
                  className="h-12 w-12 rounded-r-pill bg-surface-soft object-cover"
                />
              ) : null}
              <div className="min-w-0 flex-1">
                <h2 className="lf-h3 text-fg-1">
                  {detail.displayNameKr && detail.displayNameEn
                    ? `${detail.displayNameKr} · ${detail.displayNameEn}`
                    : detail.displayNameKr ||
                      detail.displayNameEn ||
                      detail.channelName ||
                      detail.handle}
                </h2>
                <p className="lf-body-sm lf-num text-fg-3">
                  {detail.handle} · channelId {detail.channelId.slice(0, 16)}…
                </p>
              </div>
              <span
                className={`rounded-r-1 border px-s-2 py-1 lf-eyebrow ${STATUS_BADGE[detail.status].cls}`}
              >
                {STATUS_BADGE[detail.status].label}
              </span>
            </div>
            {/* KPI grid — auto-fit ensures cards never narrow below 220px:
                desktop (1100px modal) → 4 across, tablet (720px) → 2x2,
                mobile → 1 column. items-stretch keeps heights matched
                even when one card has a hint line and another doesn't. */}
            <div
              className="mt-s-4 grid gap-s-3 items-stretch grid-cols-[repeat(auto-fit,minmax(220px,1fr))]"
            >
              <ModalKpiCard
                label={bi('연결일', 'Connected')}
                value={detail.connectedAt ? formatDate(detail.connectedAt) : '—'}
              />
              <ModalKpiCard
                label={bi('수집 행', 'Rows')}
                value={detail.collectedRowCount.toLocaleString()}
              />
              <ModalKpiCard
                label={bi('마지막 수집', 'Last collect')}
                value={detail.lastCollectedAt ?? '—'}
              />
              {detail.status === 'revoked' ? (
                // Refresh token may still be PRESENT but Google rejected it
                // (invalid_grant) — the grant is dead until re-consent. Do
                // NOT show "자동 갱신 중" here: that's the bug that made a
                // revoked grant look healthy.
                <ModalKpiCard
                  label={bi('토큰 상태', 'Token state')}
                  valueKo="갱신 실패 · 재연결 필요"
                  valueEn="Re-auth needed"
                  hint="Google rejected the refresh token (invalid_grant). Re-consent required."
                />
              ) : detail.hasRefreshToken ? (
                <ModalKpiCard
                  label={bi('토큰 상태', 'Token state')}
                  valueKo="자동 갱신 중"
                  valueEn="Auto-refresh"
                  hint="Refresh token present — access token mints on demand."
                />
              ) : (
                <ModalKpiCard
                  label={bi('토큰 만료', 'Token expiry')}
                  value={
                    detail.accessTokenExpiresAt
                      ? formatDate(detail.accessTokenExpiresAt)
                      : '—'
                  }
                />
              )}
            </div>

            {/* Debug strip: explicit refresh-token presence + auto-refresh flag.
                Small + muted so it doesn't dominate, but explicit for the operator. */}
            <p className="mt-s-3 lf-caption text-fg-3">
              ▪ Refresh Token: <span className="lf-num text-fg-2">{detail.hasRefreshToken ? '있음' : '없음'}</span>
              {' · '}
              자동 갱신: <span className="lf-num text-fg-2">{detail.hasRefreshToken && detail.status !== 'revoked' ? '활성' : '비활성'}</span>
            </p>
            {detail.lastError ? (
              <p className="mt-s-2 lf-caption text-red-700 break-all">
                ▪ 마지막 오류{detail.lastErrorAt ? ` (${formatDate(detail.lastErrorAt)})` : ''}:{' '}
                <span className="lf-num">{detail.lastError}</span>
              </p>
            ) : null}

            {/* Live token diagnostic — read-only. Hits Google in real time
                (refresh + tokeninfo + Analytics + channels.list) and prints
                the verdict + raw response. 30s cached per creator. */}
            <div className="mt-s-3 flex items-center gap-s-3">
              <button
                type="button"
                onClick={runDiag}
                disabled={diagBusy || detail.status === 'never_connected'}
                className="rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-1 lf-body-sm text-fg-1 hover:bg-surface-soft disabled:opacity-40"
              >
                {diagBusy ? '진단 중…' : '▪ 라이브 진단 실행'}
              </button>
              <span className="lf-caption text-fg-3">
                실제 Google 호출(read-only) · 30초 캐시
              </span>
            </div>

            {diagErr ? (
              <p className="mt-s-2 lf-caption text-red-700 break-all">진단 실패: {diagErr}</p>
            ) : null}

            {diag ? (
              <div className="mt-s-2 border border-line-hairline bg-surface-soft p-s-3">
                <p className="lf-body-sm text-fg-1 break-keep">{diag.verdict}</p>
                <div className="mt-s-2 grid grid-cols-2 gap-x-s-4 gap-y-1 lf-caption text-fg-2 sm:grid-cols-4">
                  <span>refresh: <b className="lf-num">{diag.refresh.ok ? 'OK' : `FAIL ${diag.refresh.httpStatus ?? ''}`}</b></span>
                  <span>analytics: <b className="lf-num">{diag.analytics.ok ? `${diag.analytics.rows ?? 0} rows` : `FAIL ${diag.analytics.httpStatus ?? ''}`}</b></span>
                  <span>channel: <b className="lf-num">{diag.channel.ok ? 'OK' : `FAIL ${diag.channel.httpStatus ?? ''}`}</b></span>
                  <span>scopes: <b className="lf-num">{diag.tokeninfo.scopes?.length ?? '—'}</b></span>
                </div>
                {diag.tokeninfo.scopes && diag.tokeninfo.scopes.length > 0 ? (
                  <p className="mt-s-2 lf-caption text-fg-3 break-all">
                    부여 scope: <span className="lf-num">{diag.tokeninfo.scopes.join(' ')}</span>
                  </p>
                ) : null}
                <details className="mt-s-2">
                  <summary className="lf-caption text-fg-3 cursor-pointer">raw 응답 보기</summary>
                  <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all rounded-r-1 bg-ink-900/90 p-s-2 lf-caption text-paper">
{JSON.stringify(diag, null, 2)}
                  </pre>
                </details>
              </div>
            ) : null}

            <h3 className="mt-s-5 lf-h4 text-fg-1">
              {bi('최근 10일 수집 이력', 'Recent 10 days')}
            </h3>
            {detail.recentCollections.length === 0 ? (
              <p className="mt-s-2 lf-body-sm text-fg-3">
                아직 Analytics 수집 이력이 없어요. (OAuth 권한은 받았지만 cron이 아직 안 돌았거나, Phase G analytics 미완성)
              </p>
            ) : (
              <div className="mt-s-2 overflow-x-auto">
                <table className="w-full border border-line-hairline">
                  <thead>
                    <tr className="border-b border-line-hairline bg-surface-soft">
                      <Th>Date</Th>
                      <Th>Views</Th>
                      <Th>Subs +/-</Th>
                      <Th>Watch (min)</Th>
                      <Th>Likes</Th>
                      <Th>Comments</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.recentCollections.map((r) => (
                      <tr key={r.date} className="border-b border-line-hairline last:border-b-0">
                        <Td>{r.date}</Td>
                        <Td>{r.views.toLocaleString()}</Td>
                        <Td>
                          +{r.subscribersGained.toLocaleString()} / -
                          {r.subscribersLost.toLocaleString()}
                        </Td>
                        <Td>{r.watchTimeMin.toLocaleString()}</Td>
                        <Td>{r.likes.toLocaleString()}</Td>
                        <Td>{r.comments.toLocaleString()}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-s-5 flex flex-wrap justify-end gap-s-2">
              <button
                type="button"
                onClick={onCollect}
                disabled={
                  busy ||
                  detail.status === 'never_connected' ||
                  detail.status === 'revoked'
                }
                className="rounded-r-1 border border-brand-pink bg-brand-pink px-s-4 py-s-2 lf-body-sm text-fg-inverse disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy ? bi('수집 중...', 'Collecting…') : bi('지금 수집', 'Collect now')}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-r-1 border border-line-hairline bg-surface-base px-s-4 py-s-2 lf-body-sm text-fg-2 hover:text-fg-1"
              >
                {bi('닫기', 'Close')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* AI Tokens — /admin/ai-tokens                                               */
/* -------------------------------------------------------------------------- */

const AI_TOKEN_BADGE: Record<AiTokenStatus, { label: string; cls: string }> = {
  active: {
    label: 'Active',
    cls: 'bg-green-50 text-green-700 border-green-200',
  },
  expired: {
    label: 'Expired',
    cls: 'bg-surface-soft text-fg-3 border-line-soft',
  },
  revoked: {
    label: 'Revoked',
    cls: 'bg-red-50 text-red-700 border-red-200',
  },
}

export function AdminAiTokensPage() {
  const [data, setData] = useState<AiTokenRow[] | null>(null)
  const [defaults, setDefaults] = useState<{ expiryDays: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)
  const [issued, setIssued] = useState<AiTokenIssueResponse | null>(null)
  const [logs, setLogs] = useState<{ row: AiTokenRow; entries: AiTokenLogRow[] } | null>(null)
  const [logsLoading, setLogsLoading] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setError(null)
    setData(null)
    fetchAiTokens()
      .then((d) => {
        if (cancelled) return
        setData(d.tokens)
        setDefaults(d.defaults)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : String(err))
      })
    return () => {
      cancelled = true
    }
  }, [tick])

  if (error?.includes('403')) return <NotAdminPanel />

  const onIssue = async (name: string, purpose: string, expiryDays: number) => {
    try {
      const r = await issueAiToken({ name, purpose, expiryDays })
      setIssued(r)
      setTick((t) => t + 1)
    } catch (err) {
      setToast(err instanceof Error ? err.message : String(err))
      setTimeout(() => setToast(null), 4000)
    }
  }

  const onRevoke = async (id: string) => {
    if (!confirm('정말 회수할까요? 회수된 토큰은 다시 활성화할 수 없어요.')) return
    try {
      await revokeAiToken(id)
      setToast('토큰을 회수했어요.')
      setTimeout(() => setToast(null), 3000)
      setTick((t) => t + 1)
    } catch (err) {
      setToast(err instanceof Error ? err.message : String(err))
      setTimeout(() => setToast(null), 4000)
    }
  }

  const openLogs = async (row: AiTokenRow) => {
    setLogs({ row, entries: [] })
    setLogsLoading(true)
    try {
      const r = await fetchAiTokenLogs(row.id)
      setLogs({ row, entries: r.logs })
    } catch (err) {
      setToast(err instanceof Error ? err.message : String(err))
      setTimeout(() => setToast(null), 4000)
    } finally {
      setLogsLoading(false)
    }
  }

  return (
    <div className="pt-s-7">
      <BackLink />
      <PageHeader
        eyebrow={bi('어드민', 'Admin')}
        title={bi('AI 토큰', 'AI Tokens')}
        onRefresh={() => setTick((t) => t + 1)}
      />
      <AdminTabs />

      {error ? (
        <p className="mt-s-6 lf-body-sm text-status-danger">▪ {error}</p>
      ) : null}
      {!data && !error ? <LoadingShell label="Loading." /> : null}

      {data ? (
        <>
          <Section title={bi('새 토큰 발급', 'Issue new token')}>
            <IssueTokenForm
              defaultExpiryDays={defaults?.expiryDays ?? 90}
              onIssue={onIssue}
            />
          </Section>

          <Section title={bi(`발급된 토큰 (${data.length})`, `Tokens (${data.length})`)}>
            {data.length === 0 ? (
              <div className="border border-dashed border-line-soft bg-surface-soft p-s-7 text-center">
                <p className="lf-eyebrow text-fg-3">▪ Empty</p>
                <p className="mt-s-2 lf-body text-fg-2">
                  아직 발급된 AI 토큰이 없어요. 위에서 첫 토큰을 발급하세요.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full border border-line-hairline">
                  <thead className="bg-surface-soft">
                    <tr>
                      <Th>Name</Th>
                      <Th>Issued</Th>
                      <Th>Expires</Th>
                      <Th>Last used</Th>
                      <Th align="right">Uses</Th>
                      <Th>Status</Th>
                      <Th>Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row) => {
                      const badge = AI_TOKEN_BADGE[row.status]
                      const expiringSoon =
                        row.status === 'active' && row.daysToExpiry <= 7
                      return (
                        <tr
                          key={row.id}
                          className="border-t border-line-hairline"
                        >
                          <Td>
                            <div className="flex flex-col">
                              <span className="text-fg-1">{row.name}</span>
                              {row.purpose ? (
                                <span className="lf-caption text-fg-3 line-clamp-1 max-w-[260px]">
                                  {row.purpose}
                                </span>
                              ) : null}
                              <span className="lf-caption text-fg-3">
                                by {row.issuedBy}
                              </span>
                            </div>
                          </Td>
                          <Td>
                            <span className="lf-num text-fg-2">{formatDate(row.issuedAt)}</span>
                          </Td>
                          <Td>
                            <div className="flex flex-col">
                              <span className="lf-num text-fg-2">{formatDate(row.expiresAt)}</span>
                              {expiringSoon ? (
                                <span className="lf-caption text-status-warning">
                                  ⚠ {row.daysToExpiry}일 남음
                                </span>
                              ) : null}
                            </div>
                          </Td>
                          <Td>
                            <span className="lf-num text-fg-2">
                              {row.lastUsedAt ? formatRelKo(row.lastUsedAt) : '—'}
                            </span>
                          </Td>
                          <Td align="right">
                            <span className="lf-num">
                              {row.usageCount.toLocaleString()}
                            </span>
                          </Td>
                          <Td>
                            <span
                              className={`inline-block rounded-r-1 border px-s-2 py-1 lf-eyebrow ${badge.cls}`}
                            >
                              {badge.label}
                            </span>
                          </Td>
                          <Td>
                            <div className="flex gap-s-2">
                              <button
                                type="button"
                                onClick={() => void openLogs(row)}
                                className="rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-1 lf-body-sm text-fg-2 hover:border-line-soft hover:text-fg-1"
                              >
                                Logs
                              </button>
                              {row.status === 'active' ? (
                                <button
                                  type="button"
                                  onClick={() => void onRevoke(row.id)}
                                  className="rounded-r-1 border border-red-200 bg-surface-base px-s-3 py-1 lf-body-sm text-red-700 hover:border-red-300 hover:bg-red-50"
                                >
                                  Revoke
                                </button>
                              ) : null}
                            </div>
                          </Td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          <Section title={bi('사용 안내', 'Usage')}>
            <div className="border border-line-hairline bg-surface-base p-s-4 lf-body-sm text-fg-2 leading-relaxed">
              <p>
                <strong>발급된 토큰</strong>은 두 가지 방식으로 사용 가능:
              </p>
              <ol className="mt-s-2 ml-s-5 list-decimal flex flex-col gap-s-1">
                <li>
                  HTTP 헤더 <code className="lf-num bg-surface-soft px-s-1">X-AI-Access-Token: &lt;token&gt;</code>
                </li>
                <li>
                  쿠키 <code className="lf-num bg-surface-soft px-s-1">ai_access_token=&lt;token&gt;</code>
                </li>
              </ol>
              <p className="mt-s-3">
                <strong>권한:</strong> 읽기 전용 (GET/HEAD/OPTIONS만 허용). POST/PUT/PATCH/DELETE는 403로 차단됨.
              </p>
              <p className="mt-s-2">
                <strong>유효 기간:</strong> {defaults?.expiryDays ?? 90}일. 만료된 토큰은 자동 인증 실패.
              </p>
              <p className="mt-s-2">
                <strong>감사 로그:</strong> 모든 요청은 90일간 기록되며 각 토큰 행의 "Logs" 버튼으로 조회 가능.
              </p>
            </div>
          </Section>
        </>
      ) : null}

      {/* === Issued token reveal modal — one-time plaintext display === */}
      {issued ? (
        <IssuedTokenModal
          issued={issued}
          onClose={() => setIssued(null)}
          onCopied={() => {
            setToast('토큰을 복사했어요.')
            setTimeout(() => setToast(null), 3000)
          }}
        />
      ) : null}

      {/* === Logs modal === */}
      {logs ? (
        <TokenLogsModal
          row={logs.row}
          entries={logs.entries}
          loading={logsLoading}
          onClose={() => setLogs(null)}
        />
      ) : null}

      {/* === Toast === */}
      {toast ? (
        <div className="fixed bottom-s-6 left-1/2 z-50 -translate-x-1/2 border border-line-soft bg-bg-ink px-s-4 py-s-3 lf-body-sm text-fg-inverse shadow-lf-1">
          {toast}
        </div>
      ) : null}
    </div>
  )
}

function IssueTokenForm({
  defaultExpiryDays,
  onIssue,
}: {
  defaultExpiryDays: number
  onIssue: (name: string, purpose: string, expiryDays: number) => void | Promise<void>
}) {
  const [name, setName] = useState('')
  const [purpose, setPurpose] = useState('')
  const [expiryDays, setExpiryDays] = useState(defaultExpiryDays)
  const [submitting, setSubmitting] = useState(false)

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    setSubmitting(true)
    try {
      await onIssue(name.trim(), purpose.trim(), expiryDays)
      setName('')
      setPurpose('')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-wrap items-end gap-s-3 border border-line-hairline bg-surface-base p-s-4"
    >
      <div className="flex flex-col gap-s-1 min-w-[240px] flex-1">
        <label className="lf-eyebrow text-fg-3">
          ▪ {bi('이름', 'Name')} <span className="text-status-danger">*</span>
        </label>
        <input
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="CHOI-VIS Production"
          maxLength={80}
          className="rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-1 placeholder:text-fg-3 focus:border-brand-pink focus:outline-none"
        />
      </div>
      <div className="flex flex-col gap-s-1 min-w-[280px] flex-[2]">
        <label className="lf-eyebrow text-fg-3">▪ {bi('용도 (메모)', 'Purpose')}</label>
        <input
          type="text"
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
          placeholder="CHOI-VIS의 reconService 듀얼뷰 캡처"
          maxLength={280}
          className="rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-1 placeholder:text-fg-3 focus:border-brand-pink focus:outline-none"
        />
      </div>
      <div className="flex flex-col gap-s-1 w-[120px]">
        <label className="lf-eyebrow text-fg-3">
          ▪ {bi('유효 (일)', 'Expiry')}
        </label>
        <input
          type="number"
          min={1}
          max={365}
          value={expiryDays}
          onChange={(e) => setExpiryDays(Math.max(1, Math.min(365, Number(e.target.value))))}
          className="rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm lf-num text-fg-1 focus:border-brand-pink focus:outline-none"
        />
      </div>
      <button
        type="submit"
        disabled={submitting || !name.trim()}
        className="rounded-r-1 border border-brand-pink bg-brand-pink px-s-4 py-s-2 lf-body-sm text-fg-inverse disabled:cursor-not-allowed disabled:opacity-50"
      >
        {submitting ? bi('발급 중...', 'Issuing…') : bi('토큰 발급', 'Issue token')}
      </button>
    </form>
  )
}

function IssuedTokenModal({
  issued,
  onClose,
  onCopied,
}: {
  issued: AiTokenIssueResponse
  onClose: () => void
  onCopied: () => void
}) {
  const copy = () => {
    void navigator.clipboard.writeText(issued.plaintext).then(onCopied, () => {
      // ignore — toast handled by caller on success only
    })
  }
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/60 px-s-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] w-full max-w-[640px] overflow-auto border-2 border-brand-pink bg-surface-base p-s-6"
      >
        <p className="lf-eyebrow text-brand-pink">▪ One-time reveal</p>
        <h2 className="mt-s-2 lf-display-2 text-fg-1">{issued.name}</h2>
        <p className="mt-s-3 lf-body-sm text-fg-2">
          <strong>{issued.warning}</strong>
        </p>
        <p className="mt-s-2 lf-body-sm text-fg-3">
          이 토큰은 SHA-256으로만 DB에 저장되며, 평문은 여기 외엔 어디에도 남지 않아요.
        </p>

        <div className="mt-s-5 border border-line-hairline bg-surface-soft p-s-4">
          <p className="lf-eyebrow text-fg-3">▪ Plaintext token</p>
          <p
            className="mt-s-2 lf-num text-fg-1 break-all"
            style={{ fontSize: 'clamp(11px, 1.4vw, 14px)', wordBreak: 'break-all' }}
          >
            {issued.plaintext}
          </p>
          <button
            type="button"
            onClick={copy}
            className="mt-s-3 rounded-r-1 border border-brand-pink bg-brand-pink px-s-4 py-s-2 lf-body-sm text-fg-inverse"
          >
            {bi('클립보드에 복사', 'Copy to clipboard')}
          </button>
        </div>

        <div className="mt-s-5 grid grid-cols-2 gap-s-3 lf-body-sm">
          <div>
            <p className="lf-eyebrow text-fg-3">Issued at</p>
            <p className="mt-s-1 lf-num">{formatDate(issued.issuedAt)}</p>
          </div>
          <div>
            <p className="lf-eyebrow text-fg-3">Expires at</p>
            <p className="mt-s-1 lf-num">{formatDate(issued.expiresAt)}</p>
          </div>
        </div>

        <div className="mt-s-6 flex justify-end gap-s-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-r-1 border border-line-hairline bg-surface-base px-s-4 py-s-2 lf-body-sm text-fg-2 hover:text-fg-1"
          >
            {bi('확인했어요', 'I saved it')}
          </button>
        </div>
      </div>
    </div>
  )
}

function TokenLogsModal({
  row,
  entries,
  loading,
  onClose,
}: {
  row: AiTokenRow
  entries: AiTokenLogRow[]
  loading: boolean
  onClose: () => void
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/60 px-s-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-[85vh] w-full max-w-[900px] overflow-auto border border-line-hairline bg-surface-base p-s-6"
      >
        <div className="flex items-start justify-between gap-s-3">
          <div>
            <p className="lf-eyebrow text-fg-3">▪ Recent logs</p>
            <h2 className="mt-s-2 lf-h3 text-fg-1">{row.name}</h2>
            <p className="lf-body-sm text-fg-3">{row.purpose}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-1 lf-body-sm text-fg-2 hover:text-fg-1"
          >
            ✕
          </button>
        </div>
        <div className="mt-s-4">
          {loading ? (
            <LoadingShell label="Loading logs." />
          ) : entries.length === 0 ? (
            <p className="lf-body-sm text-fg-3">아직 사용 로그가 없어요.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full border border-line-hairline">
                <thead className="bg-surface-soft">
                  <tr>
                    <Th>Timestamp</Th>
                    <Th>Method</Th>
                    <Th>Path</Th>
                    <Th align="right">Status</Th>
                    <Th>IP</Th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e, i) => (
                    <tr key={i} className="border-t border-line-hairline">
                      <Td>
                        <span className="lf-num text-fg-2">{formatDate(e.timestamp)}</span>
                      </Td>
                      <Td>
                        <span className="lf-num">{e.method}</span>
                      </Td>
                      <Td>
                        <span className="line-clamp-1 lf-num text-fg-2">{e.path}</span>
                      </Td>
                      <Td align="right">
                        <span
                          className={`lf-num ${
                            e.statusCode >= 200 && e.statusCode < 400
                              ? 'text-status-success'
                              : 'text-status-danger'
                          }`}
                        >
                          {e.statusCode}
                        </span>
                      </Td>
                      <Td>
                        <span className="lf-num text-fg-3">{e.ip}</span>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Creator Dashboards (Phase 4) — register / OAuth / collect / delete         */
/* -------------------------------------------------------------------------- */

function CreatorTh({ children }: { children: ReactNode }) {
  return <th className="px-s-3 py-s-2 text-left lf-eyebrow text-fg-3 whitespace-nowrap">{children}</th>
}
function CreatorActBtn({
  children,
  onClick,
  busy,
  disabled,
  danger,
}: {
  children: ReactNode
  onClick: () => void
  busy?: boolean
  disabled?: boolean
  danger?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy || disabled}
      className={`rounded-r-1 border px-s-2 py-1 lf-body-sm transition-colors disabled:opacity-40 ${
        danger
          ? 'border-line-hairline bg-surface-base text-status-danger hover:bg-surface-soft'
          : 'border-line-hairline bg-surface-base text-fg-2 hover:text-fg-1'
      }`}
    >
      {busy ? '…' : children}
    </button>
  )
}
function translateCreatorErr(code: string): string {
  if (code === 'invalid_label') return '레이블이 올바르지 않습니다.'
  if (code === 'youtubeUrl_required') return 'URL을 입력하세요.'
  if (code === 'channel_not_found') return '채널을 찾을 수 없어요. URL을 확인하세요.'
  if (code === 'quota_exceeded') return 'YouTube API 할당량 초과. 잠시 후 다시 시도하세요.'
  if (code === 'youtube_api_unconfigured') return 'YouTube API 키가 설정되지 않았어요.'
  return code
}

// Platforms shown in the channel editor, in fixed order.
const CHANNEL_PLATFORM_META: Array<{ key: ChannelPlatform; label: string; hint: string }> = [
  { key: 'youtube', label: 'YouTube', hint: 'youtube.com/@channel' },
  { key: 'instagram', label: 'Instagram', hint: 'instagram.com/username' },
  { key: 'tiktok', label: 'TikTok', hint: 'tiktok.com/@username' },
]

function fmtSubs(n: number): string {
  if (!n) return '—'
  if (n >= 1000) return Math.round(n / 1000).toLocaleString() + 'K'
  return n.toLocaleString()
}

const PLATFORM_ABBR: Record<ChannelPlatform, string> = {
  youtube: 'YT',
  instagram: 'IG',
  tiktok: 'TT',
}

/**
 * Always-3-lines subscriber summary for the creator grid: YouTube / Instagram /
 * TikTok, each with its subscriber count + OAuth/connection status. Channels
 * with no link show a warning marker so missing links pop at a glance.
 */
function ChannelSummaryLines({ creator }: { creator: AdminCreator }) {
  const channels = creator.channels ?? []
  return (
    <div className="flex flex-col gap-s-1">
      {(['youtube', 'instagram', 'tiktok'] as const).map((p) => {
        const ch = channels.find((x) => x.platform === p)
        const hasLink = !!(ch && ch.url)
        const connected = !!ch?.connected
        // Status: connected (OAuth/API) → 연동 · has link → 수동/미연동 · none → 링크없음.
        let dot = 'bg-line-strong'
        let tone = 'text-fg-3'
        let label = '수동'
        if (connected) {
          dot = 'bg-status-info'
          tone = 'text-status-info'
          label = '연동'
        } else if (p === 'youtube') {
          // YouTube always has a channel/ID; not-connected = OAuth 미연동.
          label = hasLink ? '미연동' : '링크없음'
          if (!hasLink) {
            dot = 'bg-status-warning'
            tone = 'text-status-warning'
          }
        } else if (!hasLink) {
          dot = 'bg-status-warning'
          tone = 'text-status-warning'
          label = '링크없음'
        }
        return (
          <div key={p} className="flex items-center gap-s-2 whitespace-nowrap">
            <span className="w-[18px] shrink-0 lf-eyebrow text-fg-3">{PLATFORM_ABBR[p]}</span>
            <span className="w-[46px] shrink-0 text-right lf-body-sm text-fg-1 tabular-nums">
              {ch && ch.subscriberCount ? fmtSubs(ch.subscriberCount) : '—'}
            </span>
            <span className={`inline-flex items-center gap-s-1 lf-eyebrow ${tone}`}>
              <span className={`inline-block h-[6px] w-[6px] shrink-0 rounded-r-pill ${dot}`} />
              {label}
            </span>
          </div>
        )
      })}
    </div>
  )
}

/**
 * Per-platform channel editor (one row per YouTube/Instagram/TikTok). YouTube
 * and any API-connected platform are read-only — the collector owns the number.
 * Manual platforms accept a channel link + subscriber count.
 */
function ChannelRow({
  handle,
  platform,
  label,
  hint,
  channel,
  onChanged,
  flash,
}: {
  handle: string
  platform: ChannelPlatform
  label: string
  hint: string
  channel: AdminChannel | undefined
  onChanged: () => void
  flash: (m: string) => void
}) {
  const locked = channel ? !channel.editable : false
  const [url, setUrl] = useState(channel?.url ?? '')
  const [subs, setSubs] = useState(channel && channel.subscriberCount ? String(channel.subscriberCount) : '')
  const [busy, setBusy] = useState<null | 'save' | 'remove'>(null)

  // Re-seed inputs when the underlying channel changes (after refresh).
  useEffect(() => {
    setUrl(channel?.url ?? '')
    setSubs(channel && channel.subscriberCount ? String(channel.subscriberCount) : '')
  }, [channel?.url, channel?.subscriberCount])

  async function onSave() {
    setBusy('save')
    try {
      const patch: { url?: string; subscriberCount?: number } = { url: url.trim() }
      const n = subs.trim() === '' ? undefined : Number(subs.replace(/,/g, ''))
      if (n !== undefined) {
        if (!Number.isFinite(n) || n < 0) {
          flash('구독자 수가 올바르지 않습니다.')
          setBusy(null)
          return
        }
        patch.subscriberCount = n
      }
      await putCreatorChannel(handle, platform, patch)
      flash(`${label} 채널 저장됨`)
      onChanged()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      flash(msg === 'channel_locked' ? '연동된 채널은 수동 편집할 수 없어요.' : '저장 실패: ' + msg)
    }
    setBusy(null)
  }
  async function onRemove() {
    if (!channel) return
    if (!window.confirm(`${label} 채널을 삭제할까요?`)) return
    setBusy('remove')
    try {
      await deleteCreatorChannel(handle, platform)
      flash(`${label} 채널 삭제됨`)
      onChanged()
    } catch (err) {
      flash('삭제 실패: ' + (err instanceof Error ? err.message : ''))
    }
    setBusy(null)
  }

  return (
    <div className="flex flex-wrap items-end gap-s-3 border-t border-line-hairline py-s-3 first:border-t-0">
      <div className="flex w-[110px] shrink-0 flex-col gap-s-1">
        <span className="lf-body-sm text-fg-1">{label}</span>
        <span className="lf-eyebrow whitespace-nowrap">
          {channel?.connected ? (
            <span className="text-status-info">▪ 연동됨</span>
          ) : channel ? (
            <span className="text-fg-3">▪ 수동</span>
          ) : (
            <span className="text-fg-3">▪ 미등록</span>
          )}
        </span>
      </div>

      <label className="flex min-w-[240px] flex-1 flex-col gap-s-1">
        <span className="lf-eyebrow text-fg-3">채널 링크</span>
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder={hint}
          disabled={locked}
          className="w-full rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm disabled:opacity-60"
        />
      </label>

      <label className="flex w-[130px] flex-col gap-s-1">
        <span className="lf-eyebrow text-fg-3">구독자 수</span>
        {locked ? (
          <span className="px-s-1 py-s-2 lf-body-sm text-fg-1 tabular-nums">
            {(channel?.subscriberCount ?? 0).toLocaleString()}
          </span>
        ) : (
          <input
            value={subs}
            onChange={(e) => setSubs(e.target.value)}
            inputMode="numeric"
            placeholder="0"
            className="w-full rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm tabular-nums"
          />
        )}
      </label>

      <div className="flex items-center gap-s-2 pb-s-1">
        {locked ? (
          <span className="lf-eyebrow text-fg-3 whitespace-nowrap">API 연동 · 자동 수집</span>
        ) : (
          <>
            <CreatorActBtn busy={busy === 'save'} onClick={onSave}>
              저장
            </CreatorActBtn>
            {channel && platform !== 'youtube' ? (
              <CreatorActBtn busy={busy === 'remove'} danger onClick={onRemove}>
                삭제
              </CreatorActBtn>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}

function ChannelsEditor({
  handle,
  channels,
  onChanged,
  flash,
}: {
  handle: string
  channels: AdminChannel[]
  onChanged: () => void
  flash: (m: string) => void
}) {
  const byPlatform = new Map(channels.map((c) => [c.platform, c]))
  return (
    <div className="border border-line-hairline bg-surface-soft px-s-4 py-s-2">
      <p className="pt-s-2 lf-eyebrow text-fg-3">▪ 플랫폼 채널</p>
      {CHANNEL_PLATFORM_META.map((p) => (
        <ChannelRow
          key={p.key}
          handle={handle}
          platform={p.key}
          label={p.label}
          hint={p.hint}
          channel={byPlatform.get(p.key)}
          onChanged={onChanged}
          flash={flash}
        />
      ))}
      <p className="py-s-2 lf-eyebrow text-fg-3">
        YouTube는 API로 자동 수집됩니다. Instagram·TikTok은 링크와 구독자 수를 수동 입력하며, API 연동 시 자동 전환·잠금됩니다.
      </p>
    </div>
  )
}

export function AdminCreatorsPage() {
  const [creators, setCreators] = useState<AdminCreator[] | null>(null)
  const [oauthByHandle, setOauthByHandle] = useState<Record<string, CreatorOAuthStatus>>({})
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)
  const [busy, setBusy] = useState<Record<string, string>>({})
  const [toast, setToast] = useState<string | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const [addUrl, setAddUrl] = useState('')
  const [addLabel, setAddLabel] = useState(PORTAL_LABELS[0]?.key ?? 'daead')
  const [addBusy, setAddBusy] = useState(false)
  const [addErr, setAddErr] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const toggleExpand = (h: string) =>
    setExpanded((prev) => {
      const n = new Set(prev)
      if (n.has(h)) n.delete(h)
      else n.add(h)
      return n
    })

  useEffect(() => {
    let cancelled = false
    setError(null)
    setCreators(null)
    Promise.all([
      fetchCreators(),
      fetchOAuthStatus({ showAll: true, limit: 500 }).catch(() => null),
    ])
      .then(([c, o]) => {
        if (cancelled) return
        setCreators(c.creators)
        const map: Record<string, CreatorOAuthStatus> = {}
        if (o) for (const row of o.creators) map[row.handle.toLowerCase()] = row.status
        setOauthByHandle(map)
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      })
    return () => {
      cancelled = true
    }
  }, [tick])

  const flash = (m: string) => {
    setToast(m)
    window.setTimeout(() => setToast(null), 2400)
  }
  const refresh = () => setTick((t) => t + 1)
  const setBusyFor = (h: string, v: string | null) =>
    setBusy((b) => {
      const n = { ...b }
      if (v) n[h] = v
      else delete n[h]
      return n
    })

  async function onAdd(e: FormEvent) {
    e.preventDefault()
    if (addBusy) return
    const url = addUrl.trim()
    if (!url) {
      setAddErr('URL을 입력하세요.')
      return
    }
    setAddBusy(true)
    setAddErr(null)
    try {
      const r = await postCreator(addLabel, url)
      flash((r.created ? '추가됨: ' : '갱신됨: ') + (r.creator.channelTitle || r.creator.handle))
      setAddUrl('')
      setShowAdd(false)
      refresh()
    } catch (err) {
      setAddErr(translateCreatorErr(err instanceof Error ? err.message : String(err)))
    }
    setAddBusy(false)
  }
  async function onInvite(handle: string) {
    setBusyFor(handle, 'invite')
    try {
      const r = await postOAuthInvite(handle)
      const expLabel = r.expiresAt ? ` (${r.expiresAt.slice(0, 10)} 만료)` : ''
      try {
        await navigator.clipboard.writeText(r.inviteUrl)
        flash('초대 링크를 클립보드에 복사했어요.' + expLabel)
      } catch {
        flash('초대 링크: ' + r.inviteUrl)
      }
    } catch (err) {
      flash('초대 실패: ' + (err instanceof Error ? err.message : ''))
    }
    setBusyFor(handle, null)
  }
  async function onCollect(handle: string) {
    setBusyFor(handle, 'collect')
    try {
      const r = await postOAuthCollect(handle)
      if ('error' in r) flash('이미 수집이 진행 중입니다.')
      else flash(`수집 완료 · ${r.collectedRows.toLocaleString()}행`)
      refresh()
    } catch (err) {
      flash('수집 실패: ' + (err instanceof Error ? err.message : ''))
    }
    setBusyFor(handle, null)
  }
  async function onDelete(handle: string, name: string) {
    if (!window.confirm(`"${name}" 크리에이터를 삭제할까요?\n포털 그리드에서도 사라집니다.`)) return
    setBusyFor(handle, 'delete')
    try {
      await deleteCreator(handle)
      flash('삭제됨')
      refresh()
    } catch (err) {
      flash('삭제 실패: ' + (err instanceof Error ? err.message : ''))
    }
    setBusyFor(handle, null)
  }
  async function onToggle(c: AdminCreator) {
    setBusyFor(c.handle, 'toggle')
    try {
      await patchCreatorMeta(c.handle, { dashboardEnabled: !c.dashboardEnabled })
      refresh()
    } catch (err) {
      flash('변경 실패: ' + (err instanceof Error ? err.message : ''))
      setBusyFor(c.handle, null)
    }
  }

  const filtered = (creators ?? []).filter((c) => {
    const q = filter.trim().toLowerCase()
    if (!q) return true
    return (c.displayNameKr + c.displayNameEn + c.channelTitle + c.handle).toLowerCase().includes(q)
  })

  return (
    <div className="pt-s-7">
      <BackLink />
      <PageHeader
        eyebrow={bi('어드민 · 크리에이터', 'Admin · Creator Dashboards')}
        title={bi('크리에이터 관리', 'Creator dashboards')}
        onRefresh={refresh}
      />
      <AdminTabs />

      <div className="mt-s-6">
        {!showAdd ? (
          <button
            type="button"
            onClick={() => {
              setShowAdd(true)
              setAddErr(null)
            }}
            className="rounded-r-1 border border-brand-pink bg-pink-50 px-s-4 py-s-2 lf-body-sm text-fg-1"
          >
            + 크리에이터 추가
          </button>
        ) : (
          <form onSubmit={onAdd} className="flex flex-wrap items-end gap-s-3 border border-line-hairline bg-surface-soft p-s-4">
            <label className="flex flex-col gap-s-1">
              <span className="lf-eyebrow text-fg-3">YouTube URL / @handle</span>
              <input
                value={addUrl}
                onChange={(e) => setAddUrl(e.target.value)}
                placeholder="youtube.com/@channel 또는 @channel"
                className="w-[320px] max-w-full rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm"
                autoFocus
              />
            </label>
            <label className="flex flex-col gap-s-1">
              <span className="lf-eyebrow text-fg-3">레이블</span>
              <select
                value={addLabel}
                onChange={(e) => setAddLabel(e.target.value)}
                className="rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm"
              >
                {PORTAL_LABELS.map((l) => (
                  <option key={l.key} value={l.key}>
                    {l.name}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="submit"
              disabled={addBusy}
              className="rounded-r-1 border border-brand-pink bg-brand-pink px-s-4 py-s-2 lf-body-sm text-fg-inverse disabled:opacity-50"
            >
              {addBusy ? '조회 중…' : '추가'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAdd(false)
                setAddErr(null)
              }}
              className="rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2"
            >
              취소
            </button>
            {addErr ? <p className="w-full lf-body-sm text-status-danger">▪ {addErr}</p> : null}
          </form>
        )}
      </div>

      {error ? (
        <p className="mt-s-6 lf-body-sm text-status-danger">
          ▪ {error}{' '}
          <button type="button" onClick={refresh} className="underline">
            재시도
          </button>
        </p>
      ) : null}
      {!creators && !error ? <LoadingShell label="Loading." /> : null}

      {creators && !error ? (
        creators.length === 0 ? (
          <div className="mt-s-7 border border-dashed border-line-soft bg-surface-soft p-s-9 text-center">
            <p className="lf-eyebrow text-fg-3">▪ Creators</p>
            <p className="mt-s-3 lf-h3 text-fg-1">아직 등록된 크리에이터가 없어요.</p>
            <p className="mt-s-2 lf-body-sm text-fg-3">상단 [+ 크리에이터 추가]로 YouTube 채널을 등록하세요.</p>
          </div>
        ) : (
          <>
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="채널명 / 핸들 검색"
              className="mt-s-6 w-full max-w-md rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm"
            />
            <div className="mt-s-4 overflow-x-auto">
              <table className="min-w-full border border-line-hairline">
                <thead className="bg-surface-soft">
                  <tr>
                    <CreatorTh>크리에이터</CreatorTh>
                    <CreatorTh>레이블</CreatorTh>
                    <CreatorTh>채널 · 구독자</CreatorTh>
                    <CreatorTh>OAuth</CreatorTh>
                    <CreatorTh>마지막 수집</CreatorTh>
                    <CreatorTh>대시보드</CreatorTh>
                    <CreatorTh>액션</CreatorTh>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => {
                    const st = oauthByHandle[c.handle.toLowerCase()] ?? 'never_connected'
                    const badge = STATUS_BADGE[st]
                    const b = busy[c.handle]
                    const labelName = PORTAL_LABELS.find((l) => l.key === c.label)?.name ?? (c.label || '—')
                    const name = c.displayNameKr || c.channelTitle || c.handle
                    return (
                      <Fragment key={c.handle}>
                      <tr className="border-t border-line-hairline align-middle">
                        <td className="px-s-3 py-s-3">
                          <div className="flex items-center gap-s-3">
                            {c.profileImageUrl ? (
                              <img src={c.profileImageUrl} alt="" referrerPolicy="no-referrer" className="h-9 w-9 shrink-0 rounded-r-pill object-cover" />
                            ) : (
                              <span className="inline-block h-9 w-9 shrink-0 rounded-r-pill bg-surface-soft" />
                            )}
                            <div className="min-w-0">
                              <p className="truncate lf-body-sm text-fg-1">{name}</p>
                              <p className="truncate lf-num text-fg-3" style={{ fontSize: 11 }}>{c.handle}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-s-3 py-s-3 lf-body-sm text-fg-2 whitespace-nowrap">{labelName}</td>
                        <td className="px-s-3 py-s-3 whitespace-nowrap">
                          <ChannelSummaryLines creator={c} />
                        </td>
                        <td className="px-s-3 py-s-3">
                          <span className={`inline-block rounded-r-1 border px-s-2 py-1 lf-eyebrow whitespace-nowrap ${badge.cls}`}>{badge.label}</span>
                        </td>
                        <td className="px-s-3 py-s-3 lf-body-sm text-fg-3 tabular-nums whitespace-nowrap">
                          {c.lastCollectedAt ? formatRelKo(c.lastCollectedAt) : '—'}
                        </td>
                        <td className="px-s-3 py-s-3">
                          <button
                            type="button"
                            onClick={() => onToggle(c)}
                            disabled={!!b}
                            className={`rounded-r-1 border px-s-2 py-1 lf-eyebrow disabled:opacity-40 ${
                              c.dashboardEnabled
                                ? 'border-green-200 bg-green-50 text-green-700'
                                : 'border-line-soft bg-surface-soft text-fg-3'
                            }`}
                          >
                            {b === 'toggle' ? '…' : c.dashboardEnabled ? 'ON' : 'OFF'}
                          </button>
                        </td>
                        <td className="px-s-3 py-s-3">
                          <div className="flex flex-wrap gap-s-2">
                            <CreatorActBtn onClick={() => toggleExpand(c.handle)}>
                              {expanded.has(c.handle) ? '채널 ▴' : '채널 ▾'}
                            </CreatorActBtn>
                            {st !== 'active' ? (
                              <CreatorActBtn busy={b === 'invite'} onClick={() => onInvite(c.handle)}>
                                초대
                              </CreatorActBtn>
                            ) : null}
                            <CreatorActBtn busy={b === 'collect'} onClick={() => onCollect(c.handle)}>
                              수집
                            </CreatorActBtn>
                            <a
                              href={`/dashboard/${encodeURIComponent(c.handle.replace(/^@/, ''))}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="rounded-r-1 border border-line-hairline bg-surface-base px-s-2 py-1 lf-body-sm text-fg-2 hover:text-fg-1"
                            >
                              열기
                            </a>
                            <CreatorActBtn busy={b === 'delete'} danger onClick={() => onDelete(c.handle, name)}>
                              삭제
                            </CreatorActBtn>
                          </div>
                        </td>
                      </tr>
                      {expanded.has(c.handle) ? (
                        <tr className="border-t border-line-hairline">
                          <td colSpan={7} className="px-s-3 pb-s-4">
                            <ChannelsEditor
                              handle={c.handle}
                              channels={c.channels ?? []}
                              onChanged={refresh}
                              flash={flash}
                            />
                          </td>
                        </tr>
                      ) : null}
                      </Fragment>
                    )
                  })}
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-s-3 py-s-5 lf-body-sm text-fg-3">
                        검색 결과 없음.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </>
        )
      ) : null}

      {toast ? (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-r-1 bg-fg-1 px-s-4 py-s-3 lf-body-sm text-fg-inverse shadow-lg">
          {toast}
        </div>
      ) : null}
    </div>
  )
}
